import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { db, auth } from "../../firebase.init";
import {
    collection, query, where, orderBy, limit, onSnapshot,
    doc, getDoc, getDocs, setDoc, deleteDoc, serverTimestamp, runTransaction, arrayUnion,
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHammer, faCalendarDay, faUser, faCheckCircle, faBuilding,
    faChevronDown, faChevronRight, faChevronLeft, faBan, faExclamationTriangle,
    faRotateRight, faDoorClosed, faLock, faArrowLeft, faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { Nav, Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./BookRemedial.css";


const JOB_SLOTS = { "Half day": 1, "Full day": 2 };
const DAILY_CAPACITY = 2;
const CALENDAR_MONTHS = 3;
const WEEKS_PER_PAGE = 4;
const FIRESTORE_IN_CHUNK_SIZE = 10;
const MAX_TRAVEL_MINUTES = 30;
const BUSINESS_TIME_ZONE = "Europe/London";
const ROUTE_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const CALENDAR_OWNER_UID = "rxaqnUdohkQBw3ZiH77vl2Eu4rT2";

const BOOKING_SLOT = {
    MORNING: "Morning",
    AFTERNOON: "Afternoon",
    FULL_DAY: "Full day",
};

const normalizeBookingSlot = (value, jobTime = null) => {
    const raw = String(value || "").trim().toLowerCase();

    if (jobTime === "Full day" || raw === "full day" || raw.includes("full day")) {
        return BOOKING_SLOT.FULL_DAY;
    }
    if (raw === "am" || raw.includes("morning")) return BOOKING_SLOT.MORNING;
    if (raw === "pm" || raw.includes("afternoon")) return BOOKING_SLOT.AFTERNOON;
    return null;
};

const doorCollectionName = (doorType) => (doorType === "residential" ? "ResidentialReports" : "DoorReports");


const doorLabel = (doorType, door) => (doorType === "residential" ? "R" : "C") + (door?.doorNumber || "");

const toDateId = (d) => {
    const date = d instanceof Date ? d : new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const getBusinessTodayId = () => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: BUSINESS_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
};

const dateFromId = (dateId) => {
    const [y, m, d] = String(dateId || "").split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

const useBusinessTodayId = () => {
    const [todayId, setTodayId] = useState(() => getBusinessTodayId());
    useEffect(() => {
        const timer = window.setInterval(() => {
            const next = getBusinessTodayId();
            setTodayId((current) => current === next ? current : next);
        }, 60 * 1000);
        return () => window.clearInterval(timer);
    }, []);
    return todayId;
};

const fmtDateId = (dateId) => {
    if (!dateId) return "—";
    const [y, m, d] = dateId.split("-").map(Number);
    if (!y || !m || !d) return dateId;
    return new Date(y, m - 1, d).toLocaleDateString("en-GB", {
        weekday: "short", day: "2-digit", month: "short", year: "numeric",
    });
};

const fmtDateShort = (dateId) => {
    const [y, m, d] = dateId.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return {
        weekday: dt.toLocaleDateString("en-GB", { weekday: "short" }),
        day: dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    };
};

const fmtTimestamp = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.toLocaleDateString("en-GB")} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

const getBookingCalendarDays = (months = CALENDAR_MONTHS, businessTodayId = getBusinessTodayId()) => {
    const days = [];
    const today = dateFromId(businessTodayId);
    today.setHours(0, 0, 0, 0);

    const cursor = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + months, 0);

    while (cursor <= end) {
        if (cursor.getDay() !== 0) days.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }

    return days;
};

const chunkArray = (items, size = FIRESTORE_IN_CHUNK_SIZE) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
    return chunks;
};

const bookingUrl = (buildingId, doorId) =>
    `${window.location.origin}/book-remedial/${buildingId}/${doorId}`;

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success("Link copied.");
    } catch (err) {
        console.error("Clipboard copy failed:", err);
        toast.error("Could not copy link.");
    }
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const isValidPhone = (value) => value.trim().replace(/[\s()-]/g, "").length >= 7;

const directionalRouteKey = (fromBuildingId, toBuildingId) => `${fromBuildingId}__TO__${toBuildingId}`;

const routeCacheRef = (fromBuildingId, toBuildingId) =>
    doc(db, "book-remedials", "distance-cache", "routes", directionalRouteKey(fromBuildingId, toBuildingId));

const timestampToMillis = (value) => {
    if (!value) return null;
    if (typeof value.toMillis === "function") return value.toMillis();
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
};

const isFreshRouteCacheData = (data) => {
    if (!data || typeof data.duration_minutes !== "number") return false;
    const computedAt = timestampToMillis(data.computed_at);
    if (!computedAt) return false;
    return Date.now() - computedAt <= ROUTE_CACHE_TTL_MS;
};

const buildingAddressParts = (buildingDoc) => ({
    address: [buildingDoc?.buildingName, buildingDoc?.clientAddress].filter(Boolean).join(" ").trim(),
    city: buildingDoc?.city || "",
});

const readCachedDuration = async (fromBuildingId, toBuildingId) => {
    if (fromBuildingId === toBuildingId) return 0;
    const snap = await getDoc(routeCacheRef(fromBuildingId, toBuildingId));
    if (!snap.exists()) return null;
    const data = snap.data();
    return isFreshRouteCacheData(data) ? data.duration_minutes : null;
};

const getTravelMinutesBetweenBuildings = async (fromBuildingId, fromBuildingData, toBuildingId, toBuildingData) => {
    if (fromBuildingId === toBuildingId) return 0;

    const cached = await readCachedDuration(fromBuildingId, toBuildingId);
    if (cached !== null) return cached;

    const base = buildingAddressParts(fromBuildingData);
    const dest = buildingAddressParts(toBuildingData);

    if (!base.address || !base.city || !dest.address || !dest.city) return null;

    try {
        const response = await fetch("/api/getDistance", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                baseAddress: base.address,
                baseCity: base.city,
                destAddress: dest.address,
                destCity: dest.city,
            }),
        });
        if (!response.ok) throw new Error(`Distance API returned ${response.status}`);
        const data = await response.json();
        const durationMinutes = typeof data.durationMinutes === "number" ? data.durationMinutes : null;

        if (durationMinutes !== null) {
            await setDoc(routeCacheRef(fromBuildingId, toBuildingId), {
                from_building_id: fromBuildingId,
                to_building_id: toBuildingId,
                duration_minutes: durationMinutes,
                distance_miles: data.distanceMiles ?? null,
                computed_at: serverTimestamp(),
            });
        }

        return durationMinutes;
    } catch (err) {
        console.error("getDistance call failed:", err);
        return null;
    }
};

const pairingRouteForSlot = (requestedSlot, newBuildingId, otherBuildingId) => {
    if (requestedSlot === BOOKING_SLOT.MORNING) {
        return { fromBuildingId: newBuildingId, toBuildingId: otherBuildingId };
    }
    return { fromBuildingId: otherBuildingId, toBuildingId: newBuildingId };
};

const routeFeasibilityKey = (carpenterId, dateId, requestedSlot, fromBuildingId, toBuildingId) =>
    `${carpenterId}_${dateId}_${requestedSlot}_${fromBuildingId}__TO__${toBuildingId}`; const useOwnRole = (user) => {
        const [role, setRole] = useState(null);
        const [loaded, setLoaded] = useState(false);
        useEffect(() => {
            if (!user?.uid) { setRole(null); setLoaded(true); return undefined; }
            return onSnapshot(
                doc(db, "users", user.uid),
                (snap) => { setRole(snap.exists() ? snap.data()?.user_role : null); setLoaded(true); },
                (err) => { console.error("Failed to load own role:", err); setRole(null); setLoaded(true); }
            );
        }, [user]);
        return { role, isAdmin: role === "admin" || role === "remedial" || role === "manager" || role === "coordinator", loaded };
    };

const useCarpenters = (refreshKey = 0) => {
    const [carpenters, setCarpenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        setLoading(true);
        setError(null);
        return onSnapshot(
            query(collection(db, "users"), where("user_role", "==", "carpenter")),
            (snap) => {
                setCarpenters(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error("Carpenters listener failed:", err);
                setCarpenters([]);
                setLoading(false);
                setError(err.message || "Could not load carpenter availability.");
            }
        );
    }, [refreshKey]);
    return { carpenters, loading, error };
};

const useDoorByPath = (buildingId, doorId, refreshKey = 0) => {
    const [door, setDoor] = useState(null);
    const [doorType, setDoorType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!buildingId || !doorId) { setLoading(false); return undefined; }
        let disposed = false;
        let unsubActive = null;
        let lastLookupError = null;
        setLoading(true);
        setNotFound(false);
        setError(null);

        const attach = (type) => {
            const name = doorCollectionName(type);
            const ref = doc(db, "Reports", buildingId, name, doorId);
            unsubActive = onSnapshot(
                ref,
                (snap) => {
                    if (disposed) return;
                    if (!snap.exists()) { setDoor(null); setNotFound(true); setLoading(false); return; }
                    setDoorType(type);
                    setDoor({ id: snap.id, buildingId, type, ...snap.data() });
                    setError(null);
                    setLoading(false);
                },
                (err) => {
                    console.error("Door listener failed:", err);
                    if (!disposed) {
                        setError(err.message || "Could not load this door.");
                        setLoading(false);
                    }
                }
            );
        };

        const tryCollection = (idx, types) => {
            if (idx >= types.length) {
                if (!disposed) {
                    if (lastLookupError) setError(lastLookupError.message || "Could not load this door.");
                    else setNotFound(true);
                    setLoading(false);
                }
                return;
            }
            const type = types[idx];
            const ref = doc(db, "Reports", buildingId, doorCollectionName(type), doorId);
            getDoc(ref).then((snap) => {
                if (disposed) return;
                if (snap.exists()) attach(type);
                else tryCollection(idx + 1, types);
            }).catch((err) => {
                console.error("Door lookup failed:", err);
                lastLookupError = err;
                tryCollection(idx + 1, types);
            });
        };
        tryCollection(0, ["communal", "residential"]);

        return () => { disposed = true; if (unsubActive) unsubActive(); };
    }, [buildingId, doorId, refreshKey]);

    return { door, doorType, loading, notFound, error };
};

const useBuildingName = (buildingId) => {
    const [name, setName] = useState("");
    useEffect(() => {
        if (!buildingId) { setName(""); return undefined; }
        return onSnapshot(
            doc(db, "Reports", buildingId),
            (snap) => setName(snap.exists() ? (snap.data()?.buildingName || "") : ""),
            (err) => console.error("Building name listener failed:", err)
        );
    }, [buildingId]);
    return name;
};

const useBuildingDocs = (buildingIds, refreshKey = 0) => {
    const [docsById, setDocsById] = useState({});
    const key = [...new Set(buildingIds)].sort().join(",");

    useEffect(() => {
        const ids = [...new Set(buildingIds)].filter((id) => id && (refreshKey > 0 || !docsById[id]));
        if (ids.length === 0) return;

        let disposed = false;
        Promise.all(
            ids.map((id) => getDoc(doc(db, "Reports", id))
                .then((snap) => [id, snap.exists() ? snap.data() : null])
                .catch((err) => {
                    console.error(`Failed to load building ${id} for distance check:`, err);
                    return [id, null];
                }))
        )
            .then((pairs) => {
                if (disposed) return;
                setDocsById((prev) => {
                    const next = { ...prev };
                    pairs.forEach(([id, data]) => { next[id] = data; });
                    return next;
                });
            })
            .catch((err) => {
                console.error("Failed to load building docs for distance check:", err);
                if (!disposed) {
                    setDocsById((prev) => {
                        const next = { ...prev };
                        ids.forEach((id) => { if (!(id in next)) next[id] = null; });
                        return next;
                    });
                }
            });

        return () => { disposed = true; };
    }, [key, refreshKey]);

    return docsById;
};

const useLatestRemedialInfo = (buildingId, doorId, doorType, refreshKey = 0) => {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!buildingId || !doorId || !doorType) { setInfo(null); setLoading(false); setError(null); return undefined; }
        setLoading(true);
        setError(null);
        return onSnapshot(
            query(
                collection(db, "Reports", buildingId, doorCollectionName(doorType), doorId, "remedial-info"),
                orderBy("created_at", "desc"),
                limit(1)
            ),
            (snap) => { setInfo(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() }); setLoading(false); setError(null); },
            (err) => { console.error("remedial-info listener failed:", err); setInfo(null); setLoading(false); setError(err.message || "Could not load remedial job details."); }
        );
    }, [buildingId, doorId, doorType, refreshKey]);
    return { info, loading, error };
};

const useBookRemedialsQueue = (refreshKey) => {
    const [buildingIds, setBuildingIds] = useState([]);
    const [indexLoading, setIndexLoading] = useState(true);
    const [indexError, setIndexError] = useState(null);

    useEffect(() => {
        setIndexLoading(true);
        return onSnapshot(
            doc(db, "book-remedials", "available-jobs"),
            (snap) => {
                setBuildingIds(snap.exists() ? (snap.data()?.building_ids || []) : []);
                setIndexLoading(false);
                setIndexError(null);
            },
            (err) => {
                console.error("available-jobs index listener failed:", err);
                setIndexError(err.message || "Failed to load the doors index.");
                setIndexLoading(false);
            }
        );
    }, [refreshKey]);

    const [entriesByBuilding, setEntriesByBuilding] = useState({});
    const [subLoading, setSubLoading] = useState(true);

    useEffect(() => {
        if (indexLoading) return undefined;
        if (buildingIds.length === 0) { setEntriesByBuilding({}); setSubLoading(false); return undefined; }

        setSubLoading(true);
        let pending = buildingIds.length;

        const unsubs = buildingIds.map((buildingId) =>
            onSnapshot(
                collection(db, "book-remedials", "available-jobs", buildingId),
                (snap) => {
                    setEntriesByBuilding((prev) => ({
                        ...prev,
                        [buildingId]: snap.docs.map((d) => ({
                            id: d.id,
                            buildingId,
                            doorId: d.id,
                            ...d.data(),
                        })),
                    }));
                    pending = Math.max(0, pending - 1);
                    if (pending === 0) setSubLoading(false);
                },
                (err) => {
                    console.error(`Listener failed for building ${buildingId}:`, err);
                    setEntriesByBuilding((prev) => ({ ...prev, [buildingId]: [] }));
                    pending = Math.max(0, pending - 1);
                    if (pending === 0) setSubLoading(false);
                }
            )
        );

        return () => unsubs.forEach((u) => u());
    }, [buildingIds, indexLoading, refreshKey]);

    const groupedByBuilding = useMemo(() => {
        return Object.entries(entriesByBuilding)
            .map(([buildingId, entries]) => ({ buildingId, entries }))
            .filter((g) => g.entries.length > 0);
    }, [entriesByBuilding]);

    return {
        groupedByBuilding,
        loading: indexLoading || subLoading,
        error: indexError,
    };
};

const useActiveCarpenterBookings = (refreshKey) => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        return onSnapshot(
            query(
                collection(db, "book-remedials", "bookings", "records"),
                where("status", "==", "booked"),
                orderBy("date", "asc")
            ),
            (snap) => {
                setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error("bookings listener failed:", err);
                setError(err.message || "Failed to load bookings.");
                setLoading(false);
            }
        );
    }, [refreshKey]);

    return { bookings, loading, error };
};

const useCapacityForDates = (dateIds, refreshKey = 0) => {
    const [capacityByKey, setCapacityByKey] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const key = dateIds.join(",");

    useEffect(() => {
        if (!dateIds.length) {
            setCapacityByKey({});
            setLoading(false);
            setError(null);
            return undefined;
        }

        setLoading(true);
        setError(null);
        const chunks = chunkArray(dateIds);
        const mapsByChunk = new Array(chunks.length).fill(null).map(() => ({}));
        const loadedChunks = new Set();

        const publish = () => {
            const merged = {};
            mapsByChunk.forEach((map) => Object.assign(merged, map));
            setCapacityByKey(merged);
            if (loadedChunks.size === chunks.length) setLoading(false);
        };

        const unsubs = chunks.map((chunk, index) => onSnapshot(
            query(collection(db, "book-remedials", "capacity", "slots"), where("date", "in", chunk)),
            (snap) => {
                const map = {};
                snap.docs.forEach((d) => { map[d.id] = d.data(); });
                mapsByChunk[index] = map;
                loadedChunks.add(index);
                publish();
            },
            (err) => {
                console.error("Capacity listener failed:", err);
                setError(err.message || "Live capacity could not be loaded.");
                mapsByChunk[index] = {};
                loadedChunks.add(index);
                publish();
            }
        ));

        return () => unsubs.forEach((unsub) => unsub());
    }, [key, refreshKey]);

    return { capacityByKey, loading, error };
};

const useBookingsForDates = (dateIds, refreshKey = 0) => {
    const [bookingsByKey, setBookingsByKey] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const key = dateIds.join(",");

    useEffect(() => {
        if (!dateIds.length) {
            setBookingsByKey({});
            setLoading(false);
            setError(null);
            return undefined;
        }

        setLoading(true);
        setError(null);
        const chunks = chunkArray(dateIds);
        const mapsByChunk = new Array(chunks.length).fill(null).map(() => ({}));
        const loadedChunks = new Set();

        const publish = () => {
            const merged = {};
            mapsByChunk.forEach((map) => {
                Object.entries(map).forEach(([bookingKey, rows]) => {
                    if (!merged[bookingKey]) merged[bookingKey] = [];
                    merged[bookingKey].push(...rows);
                });
            });
            setBookingsByKey(merged);
            if (loadedChunks.size === chunks.length) setLoading(false);
        };

        const unsubs = chunks.map((chunk, index) => onSnapshot(
            query(
                collection(db, "book-remedials", "bookings", "records"),
                where("status", "==", "booked"),
                where("date", "in", chunk)
            ),
            (snap) => {
                const map = {};
                snap.docs.forEach((d) => {
                    const b = d.data();
                    if (!b.carpenter_id || !b.date) return;
                    const bookingKey = `${b.carpenter_id}_${b.date}`;
                    if (!map[bookingKey]) map[bookingKey] = [];
                    map[bookingKey].push({ id: d.id, ...b });
                });
                mapsByChunk[index] = map;
                loadedChunks.add(index);
                publish();
            },
            (err) => {
                console.error("Bookings-for-dates listener failed:", err);
                setError(err.message || "Live bookings could not be loaded.");
                mapsByChunk[index] = {};
                loadedChunks.add(index);
                publish();
            }
        ));

        return () => unsubs.forEach((unsub) => unsub());
    }, [key, refreshKey]);

    return { bookingsByKey, loading, error };
};

const bookingCreatedMillis = (booking) => timestampToMillis(booking?.created_at) || 0;

const inferLegacyHalfDaySlot = (capacity = {}, booking = {}) => {
    if (!booking || booking.job_time !== "Half day") return null;

    const normalized = normalizeBookingSlot(booking.booking_slot, booking.job_time);
    if ([BOOKING_SLOT.MORNING, BOOKING_SLOT.AFTERNOON].includes(normalized)) return normalized;

    const bookingId = booking.id || null;
    const ownsMorning = Boolean(bookingId && capacity.morning_booking_id === bookingId);
    const ownsAfternoon = Boolean(bookingId && capacity.afternoon_booking_id === bookingId);
    if (ownsMorning !== ownsAfternoon) {
        return ownsMorning ? BOOKING_SLOT.MORNING : BOOKING_SLOT.AFTERNOON;
    }

    const morningFlag = capacity.morning_used === true;
    const afternoonFlag = capacity.afternoon_used === true;
    if (morningFlag !== afternoonFlag) {
        return morningFlag ? BOOKING_SLOT.MORNING : BOOKING_SLOT.AFTERNOON;
    }

    const buildingId = booking.building_id || null;
    if (buildingId) {
        const morningBuildingMatch = capacity.morning_building_id === buildingId;
        const afternoonBuildingMatch = capacity.afternoon_building_id === buildingId;
        if (morningBuildingMatch !== afternoonBuildingMatch) {
            return morningBuildingMatch ? BOOKING_SLOT.MORNING : BOOKING_SLOT.AFTERNOON;
        }
    }

    return BOOKING_SLOT.MORNING;
};

const getHalfDaySlotAssignments = (bookings = [], capacity = {}) => {
    const assigned = {
        [BOOKING_SLOT.MORNING]: null,
        [BOOKING_SLOT.AFTERNOON]: null,
    };

    const halfDays = bookings
        .filter((b) => b && b.job_time === "Half day" && (!b.status || b.status === "booked"))
        .sort((a, b) => bookingCreatedMillis(a) - bookingCreatedMillis(b) || String(a.id || "").localeCompare(String(b.id || "")));

    halfDays.forEach((b) => {
        const slot = normalizeBookingSlot(b.booking_slot, b.job_time);
        if (slot === BOOKING_SLOT.MORNING && !assigned[BOOKING_SLOT.MORNING]) assigned[BOOKING_SLOT.MORNING] = b;
        if (slot === BOOKING_SLOT.AFTERNOON && !assigned[BOOKING_SLOT.AFTERNOON]) assigned[BOOKING_SLOT.AFTERNOON] = b;
    });

    halfDays.filter((b) => !normalizeBookingSlot(b.booking_slot, b.job_time)).forEach((b) => {
        const inferredSlot = inferLegacyHalfDaySlot(capacity, b);
        if (inferredSlot && !assigned[inferredSlot]) {
            assigned[inferredSlot] = b;
            return;
        }
        const otherSlot = inferredSlot === BOOKING_SLOT.AFTERNOON ? BOOKING_SLOT.MORNING : BOOKING_SLOT.AFTERNOON;
        if (!assigned[otherSlot]) assigned[otherSlot] = b;
    });

    return assigned;
};

const capacityOwnerSlotEvidence = (activeBookings, ownerId, slot, capacity = {}) => {
    if (!ownerId) return "none";
    const booking = activeBookings.find((item) => item?.id === ownerId) || null;
    if (!booking) return "unmatched";

    const normalizedSlot = normalizeBookingSlot(booking.booking_slot, booking.job_time);
    if (booking.job_time === "Full day" || normalizedSlot === BOOKING_SLOT.FULL_DAY) {
        return "match";
    }

    if (booking.job_time === "Half day") {
        if ([BOOKING_SLOT.MORNING, BOOKING_SLOT.AFTERNOON].includes(normalizedSlot)) {
            return normalizedSlot === slot ? "match" : "conflict";
        }

        const inferredSlot = inferLegacyHalfDaySlot(capacity, booking);
        if (inferredSlot) return inferredSlot === slot ? "match" : "conflict";
        return "legacy";
    }

    return "legacy";
};

const getCarpenterDayState = (capacity = {}, existing = [], { trustUnmatchedCapacityOwners = false } = {}) => {
    const active = existing.filter((b) => b && (!b.status || b.status === "booked"));
    const assigned = getHalfDaySlotAssignments(active, capacity);
    const fullDayBooking = active.find((b) => b.job_time === "Full day") || null;

    const capacityUsed = Math.max(0, Math.min(DAILY_CAPACITY, Number(capacity.slots_used || 0)));
    const capacityMorningOwner = capacity.morning_booking_id || null;
    const capacityAfternoonOwner = capacity.afternoon_booking_id || null;
    const hasCapacityOwnership = Boolean(capacityMorningOwner || capacityAfternoonOwner);
    const hasBookingEvidence = Boolean(fullDayBooking || assigned[BOOKING_SLOT.MORNING] || assigned[BOOKING_SLOT.AFTERNOON]);

    let morningUsed = Boolean(fullDayBooking || assigned[BOOKING_SLOT.MORNING]);
    let afternoonUsed = Boolean(fullDayBooking || assigned[BOOKING_SLOT.AFTERNOON]);
    let morningBookingId = fullDayBooking?.id || assigned[BOOKING_SLOT.MORNING]?.id || null;
    let afternoonBookingId = fullDayBooking?.id || assigned[BOOKING_SLOT.AFTERNOON]?.id || null;
    let morningBuildingId = fullDayBooking?.building_id || assigned[BOOKING_SLOT.MORNING]?.building_id || null;
    let afternoonBuildingId = fullDayBooking?.building_id || assigned[BOOKING_SLOT.AFTERNOON]?.building_id || null;

    const activeBookingIds = new Set(active.map((booking) => booking.id).filter(Boolean));
    const morningOwnerEvidence = capacityOwnerSlotEvidence(active, capacityMorningOwner, BOOKING_SLOT.MORNING, capacity);
    const afternoonOwnerEvidence = capacityOwnerSlotEvidence(active, capacityAfternoonOwner, BOOKING_SLOT.AFTERNOON, capacity);

    const canTrustMorningOwner = morningOwnerEvidence !== "conflict" && (
        morningOwnerEvidence === "match"
        || morningOwnerEvidence === "legacy"
        || trustUnmatchedCapacityOwners
        || !hasBookingEvidence
        || activeBookingIds.has(capacityMorningOwner)
    );
    const canTrustAfternoonOwner = afternoonOwnerEvidence !== "conflict" && (
        afternoonOwnerEvidence === "match"
        || afternoonOwnerEvidence === "legacy"
        || trustUnmatchedCapacityOwners
        || !hasBookingEvidence
        || activeBookingIds.has(capacityAfternoonOwner)
    );

    if (!morningUsed && capacityMorningOwner && canTrustMorningOwner) {
        morningUsed = true;
        morningBookingId = capacityMorningOwner;
        morningBuildingId = capacity.morning_building_id || null;
    }
    if (!afternoonUsed && capacityAfternoonOwner && canTrustAfternoonOwner) {
        afternoonUsed = true;
        afternoonBookingId = capacityAfternoonOwner;
        afternoonBuildingId = capacity.afternoon_building_id || null;
    }

    if (morningBookingId && morningBookingId === afternoonBookingId) {
        const duplicatedBooking = active.find((booking) => booking.id === morningBookingId) || null;
        if (duplicatedBooking?.job_time === "Half day") {
            const resolvedSlot = inferLegacyHalfDaySlot(capacity, duplicatedBooking);
            if (resolvedSlot === BOOKING_SLOT.AFTERNOON) {
                morningUsed = false;
                morningBookingId = null;
                morningBuildingId = null;
            } else {
                afternoonUsed = false;
                afternoonBookingId = null;
                afternoonBuildingId = null;
            }
        }
    }

    if (!hasBookingEvidence && !hasCapacityOwnership) {
        const hasExplicitBooleans =
            typeof capacity.morning_used === "boolean" || typeof capacity.afternoon_used === "boolean";
        if (hasExplicitBooleans) {
            morningUsed = Boolean(capacity.morning_used);
            afternoonUsed = Boolean(capacity.afternoon_used);
            morningBuildingId = morningUsed ? (capacity.morning_building_id || null) : null;
            afternoonBuildingId = afternoonUsed ? (capacity.afternoon_building_id || null) : null;
        } else if (capacityUsed > 0) {
            morningUsed = capacityUsed >= 1;
            afternoonUsed = capacityUsed >= 2;
            morningBuildingId = morningUsed ? (capacity.morning_building_id || null) : null;
            afternoonBuildingId = afternoonUsed ? (capacity.afternoon_building_id || null) : null;
        }
    }

    return {
        morningUsed,
        afternoonUsed,
        morningBookingId,
        afternoonBookingId,
        morningBuildingId,
        afternoonBuildingId,
        usedCount: Number(morningUsed) + Number(afternoonUsed),
        fullDayBooking,
    };
};

const getTransactionCarpenterDayState = (capacity = {}, existing = []) => {
    if (Number(capacity.schema_version || 0) >= 2) {
        const active = existing.filter((b) => b && (!b.status || b.status === "booked"));
        const hasBookingEvidence = active.length > 0;

        let morningBookingId = capacity.morning_booking_id || null;
        let afternoonBookingId = capacity.afternoon_booking_id || null;
        let morningBuildingId = capacity.morning_building_id || null;
        let afternoonBuildingId = capacity.afternoon_building_id || null;

        const morningOwnerEvidence = capacityOwnerSlotEvidence(active, morningBookingId, BOOKING_SLOT.MORNING, capacity);
        const afternoonOwnerEvidence = capacityOwnerSlotEvidence(active, afternoonBookingId, BOOKING_SLOT.AFTERNOON, capacity);

        if (morningOwnerEvidence === "conflict") {
            morningBookingId = null;
            morningBuildingId = null;
        }
        if (afternoonOwnerEvidence === "conflict") {
            afternoonBookingId = null;
            afternoonBuildingId = null;
        }

        if (morningBookingId && morningBookingId === afternoonBookingId) {
            const duplicatedBooking = active.find((booking) => booking.id === morningBookingId) || null;
            if (duplicatedBooking?.job_time === "Half day") {
                const resolvedSlot = inferLegacyHalfDaySlot(capacity, duplicatedBooking);
                if (resolvedSlot === BOOKING_SLOT.AFTERNOON) {
                    morningBookingId = null;
                    morningBuildingId = null;
                } else {
                    afternoonBookingId = null;
                    afternoonBuildingId = null;
                }
            }
        }

        const morningUsed = Boolean(morningBookingId) || (!hasBookingEvidence && Boolean(capacity.morning_used));
        const afternoonUsed = Boolean(afternoonBookingId) || (!hasBookingEvidence && Boolean(capacity.afternoon_used));

        return {
            morningUsed,
            afternoonUsed,
            morningBookingId: morningUsed ? morningBookingId : null,
            afternoonBookingId: afternoonUsed ? afternoonBookingId : null,
            morningBuildingId: morningUsed ? morningBuildingId : null,
            afternoonBuildingId: afternoonUsed ? afternoonBuildingId : null,
            usedCount: Number(morningUsed) + Number(afternoonUsed),
            fullDayBooking: null,
        };
    }
    return getCarpenterDayState(capacity, existing, { trustUnmatchedCapacityOwners: true });
};

const buildCarpenterLoadById = (carpenters, bookingsByKey) => {
    const loadById = {};
    carpenters.forEach((c) => { loadById[c.uid] = 0; });

    Object.values(bookingsByKey).forEach((list) => {
        list.forEach((booking) => {
            if (!booking?.carpenter_id) return;
            const slots = Number(booking.slots_used || JOB_SLOTS[booking.job_time] || 1);
            loadById[booking.carpenter_id] = (loadById[booking.carpenter_id] || 0) + slots;
        });
    });

    return loadById;
};

const candidateRelationPriority = { same: 0, near: 1, open: 2 };

const sortAssignmentCandidates = (candidates) => [...candidates].sort((a, b) => {
    const relationDiff = (candidateRelationPriority[a.relation] ?? 99) - (candidateRelationPriority[b.relation] ?? 99);
    if (relationDiff !== 0) return relationDiff;

    if (a.relation === "near" && b.relation === "near") {
        const minutesA = typeof a.minutes === "number" ? a.minutes : Number.POSITIVE_INFINITY;
        const minutesB = typeof b.minutes === "number" ? b.minutes : Number.POSITIVE_INFINITY;
        if (minutesA !== minutesB) return minutesA - minutesB;
    }

    const loadDiff = Number(a.loadScore || 0) - Number(b.loadScore || 0);
    if (loadDiff !== 0) return loadDiff;

    return String(a.carpenter?.uid || "").localeCompare(String(b.carpenter?.uid || ""));
});

const getLiveHalfDayAvailability = ({
    carpenters,
    dateId,
    requestedSlot,
    capacityByKey,
    bookingsByKey,
    buildingId,
    feasibilityByKey,
    carpenterLoadById = {},
}) => {
    const candidates = [];
    let usedCount = 0;
    let blockedCount = 0;
    let checkingCount = 0;
    let verificationErrorCount = 0;

    carpenters.forEach((c) => {
        const key = `${c.uid}_${dateId}`;
        const capacity = capacityByKey[key] || {};
        const existing = bookingsByKey[key] || [];
        const state = getCarpenterDayState(capacity, existing);
        const loadScore = Number(carpenterLoadById[c.uid] || 0);

        const requestedUsed = requestedSlot === BOOKING_SLOT.MORNING
            ? state.morningUsed
            : state.afternoonUsed;
        if (requestedUsed) {
            usedCount += 1;
            return;
        }

        const otherSlot = requestedSlot === BOOKING_SLOT.MORNING
            ? BOOKING_SLOT.AFTERNOON
            : BOOKING_SLOT.MORNING;
        const otherUsed = otherSlot === BOOKING_SLOT.MORNING
            ? state.morningUsed
            : state.afternoonUsed;
        const otherBuildingId = otherSlot === BOOKING_SLOT.MORNING
            ? state.morningBuildingId
            : state.afternoonBuildingId;

        if (!otherUsed) {
            candidates.push({ carpenter: c, relation: "open", minutes: null, loadScore });
            return;
        }

        if (!otherBuildingId) {
            blockedCount += 1;
            return;
        }

        if (otherBuildingId === buildingId) {
            candidates.push({ carpenter: c, relation: "same", minutes: 0, loadScore });
            return;
        }

        const route = pairingRouteForSlot(requestedSlot, buildingId, otherBuildingId);
        const feasibilityKey = routeFeasibilityKey(
            c.uid,
            dateId,
            requestedSlot,
            route.fromBuildingId,
            route.toBuildingId
        );
        const feasibility = feasibilityByKey[feasibilityKey];
        if (!feasibility || feasibility.status === "checking") {
            checkingCount += 1;
            return;
        }

        if (feasibility.status === "checking-error") {
            verificationErrorCount += 1;
            return;
        }

        if (feasibility.status === "near") {
            candidates.push({ carpenter: c, relation: "near", minutes: feasibility.minutes, loadScore });
            return;
        }

        blockedCount += 1;
    });

    const sortedCandidates = sortAssignmentCandidates(candidates);

    return {
        candidates: sortedCandidates,
        candidate: sortedCandidates[0] || null,
        availableCount: sortedCandidates.length,
        totalCount: carpenters.length,
        usedCount,
        blockedCount,
        checkingCount,
        verificationErrorCount,
        canBook: sortedCandidates.length > 0,
        checking: sortedCandidates.length === 0 && checkingCount > 0,
        verificationError: sortedCandidates.length === 0 && checkingCount === 0 && verificationErrorCount > 0,
        blockedByTravelRule: sortedCandidates.length === 0 && checkingCount === 0 && verificationErrorCount === 0 && blockedCount > 0,
    };
};

const pickFeaturedSlotKeys = (rankedSlots, limit = 3) => {
    const picked = [];
    const pickedKeys = new Set();
    const pickedDates = new Set();

    rankedSlots.forEach((entry) => {
        if (picked.length >= limit || pickedDates.has(entry.dateId)) return;
        picked.push(entry.slotKey);
        pickedKeys.add(entry.slotKey);
        pickedDates.add(entry.dateId);
    });

    rankedSlots.forEach((entry) => {
        if (picked.length >= limit || pickedKeys.has(entry.slotKey)) return;
        picked.push(entry.slotKey);
        pickedKeys.add(entry.slotKey);
    });

    return picked;
};

const buildPrecalculatedHalfDaySuitability = ({
    carpenters,
    dateIds,
    todayId,
    capacityByKey,
    bookingsByKey,
    buildingId,
    feasibilityByKey,
    carpenterLoadById,
}) => {
    const bySlotKey = {};
    const rankedSlots = [];
    let pendingRouteChecks = 0;

    dateIds.forEach((dateId) => {
        if (dateId <= todayId) return;

        [BOOKING_SLOT.MORNING, BOOKING_SLOT.AFTERNOON].forEach((requestedSlot) => {
            const slotKey = `${dateId}_${requestedSlot}`;
            const raw = getLiveHalfDayAvailability({
                carpenters,
                dateId,
                requestedSlot,
                capacityByKey,
                bookingsByKey,
                buildingId,
                feasibilityByKey,
                carpenterLoadById,
            });

            const bestRelation = raw.candidate?.relation || null;
            const bestMinutes = raw.candidate?.minutes ?? null;
            const bestLoad = raw.candidate?.loadScore ?? Number.POSITIVE_INFINITY;

            bySlotKey[slotKey] = {
                ...raw,
                slotKey,
                dateId,
                requestedSlot,
                bestRelation,
                choiceLabel: raw.canBook ? "available" : null,
                recommendationRank: null,
            };

            pendingRouteChecks += raw.checkingCount || 0;

            if (raw.canBook) {
                rankedSlots.push({
                    slotKey,
                    dateId,
                    requestedSlot,
                    relation: bestRelation,
                    minutes: bestMinutes,
                    loadScore: bestLoad,
                });
            }
        });
    });

    rankedSlots.sort((a, b) => {
        const relationDiff = (candidateRelationPriority[a.relation] ?? 99) - (candidateRelationPriority[b.relation] ?? 99);
        if (relationDiff !== 0) return relationDiff;

        if (a.relation === "near" && b.relation === "near") {
            const minuteDiff = Number(a.minutes ?? Number.POSITIVE_INFINITY) - Number(b.minutes ?? Number.POSITIVE_INFINITY);
            if (minuteDiff !== 0) return minuteDiff;
        }

        const dateDiff = a.dateId.localeCompare(b.dateId);
        if (dateDiff !== 0) return dateDiff;

        const slotOrder = { [BOOKING_SLOT.MORNING]: 0, [BOOKING_SLOT.AFTERNOON]: 1 };
        const slotDiff = (slotOrder[a.requestedSlot] ?? 99) - (slotOrder[b.requestedSlot] ?? 99);
        if (slotDiff !== 0) return slotDiff;

        return Number(a.loadScore || 0) - Number(b.loadScore || 0);
    });

    const featuredSlotKeys = pickFeaturedSlotKeys(rankedSlots, 3);
    featuredSlotKeys.forEach((slotKey, index) => {
        if (!bySlotKey[slotKey]) return;
        bySlotKey[slotKey] = {
            ...bySlotKey[slotKey],
            choiceLabel: index === 0 ? "recommended" : "good",
            recommendationRank: index + 1,
        };
    });

    return {
        bySlotKey,
        rankedSlots,
        featuredSlotKeys,
        recommendedSlotKey: featuredSlotKeys[0] || null,
        goodSlotKeys: featuredSlotKeys.slice(1),
        pendingRouteChecks,
        ready: true,
    };
};

const getLiveFullDayAvailability = ({
    carpenters,
    dateId,
    capacityByKey,
    bookingsByKey,
    carpenterLoadById = {},
}) => {
    const candidates = [];
    let busyCount = 0;

    carpenters.forEach((c) => {
        const key = `${c.uid}_${dateId}`;
        const capacity = capacityByKey[key] || {};
        const existing = bookingsByKey[key] || [];
        const state = getCarpenterDayState(capacity, existing);

        if (!state.morningUsed && !state.afternoonUsed && existing.length === 0) {
            candidates.push({
                carpenter: c,
                relation: "open",
                minutes: null,
                loadScore: Number(carpenterLoadById[c.uid] || 0),
            });
        } else {
            busyCount += 1;
        }
    });

    const sortedCandidates = sortAssignmentCandidates(candidates);

    return {
        candidates: sortedCandidates,
        candidate: sortedCandidates[0] || null,
        availableCount: sortedCandidates.length,
        totalCount: carpenters.length,
        busyCount,
        canBook: sortedCandidates.length > 0,
        checking: false,
    };
};

const buildPrecalculatedFullDaySuitability = ({
    carpenters,
    dateIds,
    todayId,
    capacityByKey,
    bookingsByKey,
    carpenterLoadById,
}) => {
    const byDateId = {};
    const rankedDates = [];

    dateIds.forEach((dateId) => {
        if (dateId <= todayId) return;

        const availability = getLiveFullDayAvailability({
            carpenters,
            dateId,
            capacityByKey,
            bookingsByKey,
            carpenterLoadById,
        });

        byDateId[dateId] = {
            ...availability,
            choiceLabel: availability.canBook ? "available" : null,
            recommendationRank: null,
        };

        if (availability.canBook) rankedDates.push(dateId);
    });

    rankedDates.sort((a, b) => a.localeCompare(b));
    rankedDates.slice(0, 3).forEach((dateId, index) => {
        byDateId[dateId] = {
            ...byDateId[dateId],
            choiceLabel: index === 0 ? "recommended" : "good",
            recommendationRank: index + 1,
        };
    });

    return {
        byDateId,
        featuredDateIds: rankedDates.slice(0, 3),
    };
};

const bookRemedialTx = async ({
    buildingId, doorType, doorId, doorNumber, buildingName,
    jobTime, bookingSlot, carpenter, dateId, notes, actorUid, bookedByName,
    contactEmail, contactPhone, calendarEventBase,
}) => {
    const needed = JOB_SLOTS[jobTime] || 1;
    const normalizedSlot = jobTime === "Full day" ? BOOKING_SLOT.FULL_DAY : bookingSlot;

    if (jobTime === "Half day" && ![BOOKING_SLOT.MORNING, BOOKING_SLOT.AFTERNOON].includes(normalizedSlot)) {
        throw new Error("Please choose a morning or afternoon slot.");
    }

    const capacityId = `${carpenter.uid}_${dateId}`;
    const capacityRef = doc(db, "book-remedials", "capacity", "slots", capacityId);
    const bookingRef = doc(collection(db, "book-remedials", "bookings", "records"));
    const calendarRef = doc(db, "users", CALENDAR_OWNER_UID, "calendar", bookingRef.id);
    const doorRef = doc(db, "Reports", buildingId, doorCollectionName(doorType), doorId);
    const pointerRef = doc(db, "book-remedials", "available-jobs", buildingId, doorId);

    let existingBookings = [];
    if (jobTime === "Half day") {
        const existingSnap = await getDocs(
            query(
                collection(db, "book-remedials", "bookings", "records"),
                where("status", "==", "booked"),
                where("carpenter_id", "==", carpenter.uid),
                where("date", "==", dateId)
            )
        );
        existingBookings = existingSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }

    await runTransaction(db, async (tx) => {
        const [capacitySnap, doorSnap] = await Promise.all([tx.get(capacityRef), tx.get(doorRef)]);

        if (!doorSnap.exists()) throw new Error("This door no longer exists.");
        if (doorSnap.data()?.remedial_booking) throw new Error("This door has already been booked by someone else.");

        const capacityData = capacitySnap.exists() ? capacitySnap.data() : {};
        const state = getTransactionCarpenterDayState(capacityData, existingBookings);
        let morningUsed = state.morningUsed;
        let afternoonUsed = state.afternoonUsed;
        let morningBookingId = state.morningBookingId;
        let afternoonBookingId = state.afternoonBookingId;
        let morningBuildingId = state.morningBuildingId;
        let afternoonBuildingId = state.afternoonBuildingId;
        const usedSlots = state.usedCount;

        if (jobTime === "Full day") {
            if (morningUsed || afternoonUsed || usedSlots > 0) {
                throw new Error("This date is no longer available. Please choose another date.");
            }
        } else if (normalizedSlot === BOOKING_SLOT.MORNING && morningUsed) {
            throw new Error("The morning slot is no longer available. Please choose another slot.");
        } else if (normalizedSlot === BOOKING_SLOT.AFTERNOON && afternoonUsed) {
            throw new Error("The afternoon slot is no longer available. Please choose another slot.");
        }

        if (usedSlots + needed > DAILY_CAPACITY) {
            throw new Error("This date no longer has enough availability. Please choose another slot.");
        }

        if (jobTime === "Half day" && usedSlots === 1) {
            const otherSlot = normalizedSlot === BOOKING_SLOT.MORNING ? BOOKING_SLOT.AFTERNOON : BOOKING_SLOT.MORNING;
            const otherBuildingId = otherSlot === BOOKING_SLOT.MORNING ? morningBuildingId : afternoonBuildingId;

            if (!otherBuildingId) {
                throw new Error("This slot cannot be matched safely with the other half-day job. Please choose another slot.");
            }

            if (otherBuildingId !== buildingId) {
                const route = pairingRouteForSlot(normalizedSlot, buildingId, otherBuildingId);
                const cacheSnap = await tx.get(routeCacheRef(route.fromBuildingId, route.toBuildingId));
                const cacheData = cacheSnap.exists() ? cacheSnap.data() : null;
                const cachedMinutes = isFreshRouteCacheData(cacheData) ? cacheData.duration_minutes : null;

                if (typeof cachedMinutes !== "number") {
                    throw new Error("This route must be rechecked before the slot can be booked.");
                }
                if (cachedMinutes > MAX_TRAVEL_MINUTES) {
                    throw new Error("This slot is only available when the other half-day job is at the same building or within 30 minutes.");
                }
            }
        }

        if (jobTime === "Full day") {
            morningUsed = true;
            afternoonUsed = true;
            morningBookingId = bookingRef.id;
            afternoonBookingId = bookingRef.id;
            morningBuildingId = buildingId;
            afternoonBuildingId = buildingId;
        } else if (normalizedSlot === BOOKING_SLOT.MORNING) {
            morningUsed = true;
            morningBookingId = bookingRef.id;
            morningBuildingId = buildingId;
        } else {
            afternoonUsed = true;
            afternoonBookingId = bookingRef.id;
            afternoonBuildingId = buildingId;
        }

        tx.set(capacityRef, {
            carpenter_id: carpenter.uid,
            carpenter_name: carpenter.name || carpenter.email,
            date: dateId,
            capacity: DAILY_CAPACITY,
            morning_used: morningUsed,
            afternoon_used: afternoonUsed,
            morning_booking_id: morningBookingId || null,
            afternoon_booking_id: afternoonBookingId || null,
            morning_building_id: morningUsed ? (morningBuildingId || null) : null,
            afternoon_building_id: afternoonUsed ? (afternoonBuildingId || null) : null,
            slots_used: Number(morningUsed) + Number(afternoonUsed),
            schema_version: 2,
            updated_at: serverTimestamp(),
        }, { merge: true });

        tx.set(bookingRef, {
            building_id: buildingId,
            building_name: buildingName,
            door_id: doorId,
            door_type: doorType,
            door_number: doorNumber,
            job_time: jobTime,
            booking_slot: normalizedSlot,
            slots_used: needed,
            carpenter_id: carpenter.uid,
            carpenter_name: carpenter.name || carpenter.email,
            date: dateId,
            notes: notes?.trim() || null,
            status: "booked",
            capacity_doc_id: capacityId,
            calendar_event_id: bookingRef.id,
            created_by: actorUid || "guest",
            created_by_name: bookedByName || null,
            contact_email: contactEmail?.trim() || null,
            contact_phone: contactPhone?.trim() || null,
            created_at: serverTimestamp(),
        });

        tx.set(calendarRef, {
            ...calendarEventBase,
            carpenterName: carpenter.name || carpenter.email || "",
            assignedCarpenter: carpenter.uid,
            sourceBookingId: bookingRef.id,
            created_at: serverTimestamp(),
        });

        tx.update(doorRef, {
            remedial_booking: {
                booking_id: bookingRef.id,
                date: dateId,
                job_time: jobTime,
                booking_slot: normalizedSlot,
                carpenter_id: carpenter.uid,
            },
        });

        tx.delete(pointerRef);
    });

    return bookingRef.id;
};

const cancelRemedialBookingTx = async (booking) => {
    const bookingRef = doc(db, "book-remedials", "bookings", "records", booking.id);
    let cancelledBookingData = null;

    await runTransaction(db, async (tx) => {

        const bookingSnap = await tx.get(bookingRef);
        if (!bookingSnap.exists()) throw new Error("This booking no longer exists. Refresh and try again.");

        const liveBooking = { id: bookingSnap.id, ...bookingSnap.data() };
        if (liveBooking.status !== "booked") throw new Error("This booking has already been cancelled or changed. Refresh the list.");
        cancelledBookingData = liveBooking;

        const capacityId = liveBooking.capacity_doc_id || `${liveBooking.carpenter_id}_${liveBooking.date}`;
        const capacityRef = doc(db, "book-remedials", "capacity", "slots", capacityId);
        const calendarRef = doc(db, "users", CALENDAR_OWNER_UID, "calendar", liveBooking.calendar_event_id || liveBooking.id);
        const doorRef = (liveBooking.building_id && liveBooking.door_id)
            ? doc(db, "Reports", liveBooking.building_id, doorCollectionName(liveBooking.door_type), liveBooking.door_id)
            : null;
        const pointerRef = (liveBooking.building_id && liveBooking.door_id)
            ? doc(db, "book-remedials", "available-jobs", liveBooking.building_id, liveBooking.door_id)
            : null;

        const [capacitySnap, doorSnap] = await Promise.all([
            tx.get(capacityRef),
            doorRef ? tx.get(doorRef) : Promise.resolve(null),
        ]);

        const capacityData = capacitySnap.exists() ? capacitySnap.data() : {};
        const hasModernOwners = Boolean(capacityData.morning_booking_id || capacityData.afternoon_booking_id);
        let slot = normalizeBookingSlot(liveBooking.booking_slot, liveBooking.job_time);
        if (!slot && capacityData.morning_booking_id === liveBooking.id) slot = BOOKING_SLOT.MORNING;
        if (!slot && capacityData.afternoon_booking_id === liveBooking.id) slot = BOOKING_SLOT.AFTERNOON;
        if (!slot && liveBooking.job_time === "Half day") slot = BOOKING_SLOT.MORNING;

        let morningUsed = Boolean(capacityData.morning_used || capacityData.morning_booking_id);
        let afternoonUsed = Boolean(capacityData.afternoon_used || capacityData.afternoon_booking_id);
        let morningBookingId = capacityData.morning_booking_id || null;
        let afternoonBookingId = capacityData.afternoon_booking_id || null;
        let morningBuildingId = capacityData.morning_building_id || null;
        let afternoonBuildingId = capacityData.afternoon_building_id || null;

        if (liveBooking.job_time === "Full day" || slot === BOOKING_SLOT.FULL_DAY) {
            if (hasModernOwners && (morningBookingId !== liveBooking.id || afternoonBookingId !== liveBooking.id)) {
                throw new Error("This booking no longer owns both day slots. Refresh before cancelling.");
            }
            morningUsed = false;
            afternoonUsed = false;
            morningBookingId = null;
            afternoonBookingId = null;
            morningBuildingId = null;
            afternoonBuildingId = null;
        } else if (slot === BOOKING_SLOT.MORNING) {
            if (hasModernOwners && morningBookingId !== liveBooking.id) {
                throw new Error("This booking no longer owns the morning slot. Refresh before cancelling.");
            }
            morningUsed = false;
            morningBookingId = null;
            morningBuildingId = null;
        } else if (slot === BOOKING_SLOT.AFTERNOON) {
            if (hasModernOwners && afternoonBookingId !== liveBooking.id) {
                throw new Error("This booking no longer owns the afternoon slot. Refresh before cancelling.");
            }
            afternoonUsed = false;
            afternoonBookingId = null;
            afternoonBuildingId = null;
        } else {
            throw new Error("Could not determine which slot this booking owns. Refresh before cancelling.");
        }

        tx.set(capacityRef, {
            morning_used: morningUsed,
            afternoon_used: afternoonUsed,
            morning_booking_id: morningBookingId,
            afternoon_booking_id: afternoonBookingId,
            morning_building_id: morningUsed ? morningBuildingId : null,
            afternoon_building_id: afternoonUsed ? afternoonBuildingId : null,
            slots_used: Number(morningUsed) + Number(afternoonUsed),
            schema_version: 2,
            updated_at: serverTimestamp(),
        }, { merge: true });

        tx.update(bookingRef, { status: "cancelled", cancelled_at: serverTimestamp() });
        tx.delete(calendarRef);

        if (doorRef && doorSnap?.exists()) {
            const doorData = doorSnap.data();
            const doorOwnsThisBooking = doorData?.remedial_booking?.booking_id === liveBooking.id;
            if (doorOwnsThisBooking) {
                tx.update(doorRef, { remedial_booking: null });

                const alreadyDone = Boolean(doorData.workDone) || Boolean(doorData.workDoneUpdatedAt);
                if (pointerRef && !alreadyDone && !doorData.archived) {
                    tx.set(pointerRef, {
                        door_type: liveBooking.door_type,
                        updated_at: serverTimestamp(),
                    });
                }
            }
        }
    });

    if (cancelledBookingData?.building_id) {
        try {
            await setDoc(
                doc(db, "book-remedials", "available-jobs"),
                { building_ids: arrayUnion(cancelledBookingData.building_id) },
                { merge: true }
            );
        } catch (err) {
            console.log("Could not refresh building index after cancel:", err);
        }
    }

    if (cancelledBookingData && !cancelledBookingData.calendar_event_id && cancelledBookingData.carpenter_id) {
        try {
            const expected = getRemedialSlotTimes(
                cancelledBookingData.job_time,
                cancelledBookingData.date,
                cancelledBookingData.booking_slot
            );
            const calendarSnap = await getDocs(
                query(
                    collection(db, "users", CALENDAR_OWNER_UID, "calendar"),
                    where("assignedCarpenter", "==", cancelledBookingData.carpenter_id)
                )
            );
            const titlePrefix = `${cancelledBookingData.door_number} ·`;
            const matches = calendarSnap.docs.filter((calendarDoc) => {
                const event = calendarDoc.data();
                return event.type === "remedial"
                    && event.start === expected.start
                    && String(event.title || "").startsWith(titlePrefix);
            });
            await Promise.all(matches.map((calendarDoc) => deleteDoc(calendarDoc.ref)));
        } catch (err) {
            console.log("Could not clean up a legacy calendar event after cancellation:", err);
        }
    }
};

const buildCalendarDateTime = (dateId, hh, mm) => {
    const [y, m, d] = dateId.split("-");
    return `${d}/${m}/${y} ${hh}:${mm}:00`;
};

const getRemedialSlotTimes = (jobTime, dateId, bookingSlot) => {
    if (jobTime === "Full day" || bookingSlot === BOOKING_SLOT.FULL_DAY) {
        return {
            start: buildCalendarDateTime(dateId, "08", "30"),
            end: buildCalendarDateTime(dateId, "16", "30"),
        };
    }
    if (bookingSlot === BOOKING_SLOT.AFTERNOON) {
        return {
            start: buildCalendarDateTime(dateId, "12", "30"),
            end: buildCalendarDateTime(dateId, "16", "30"),
        };
    }
    return {
        start: buildCalendarDateTime(dateId, "08", "30"),
        end: buildCalendarDateTime(dateId, "12", "30"),
    };
};

const toTitleCase = (str) =>
    str.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const QueueDoorRow = ({ entry, onBuildingName }) => {
    const { door, doorType, loading: doorLoading } = useDoorByPath(entry.buildingId, entry.doorId);
    const { info: remedialInfo, loading: infoLoading } = useLatestRemedialInfo(
        entry.buildingId, entry.doorId, doorType || entry.door_type
    );
    const buildingName = useBuildingName(entry.buildingId);

    useEffect(() => {
        if (buildingName) onBuildingName?.(entry.buildingId, buildingName);
    }, [buildingName, entry.buildingId, onBuildingName]);

    if (doorLoading || infoLoading) {
        return (
            <div className="remedial-door-row">
                <div style={{ minWidth: 0 }}>
                    <div className="remedial-door-title" style={{ color: "#cbd5e1" }}>Loading…</div>
                </div>
            </div>
        );
    }

    if (!door) return null;
    if (door.archived) return null;
    if (Boolean(door.workDone) || Boolean(door.workDoneUpdatedAt)) return null;
    if (door.remedial_booking) return null;
    if (!remedialInfo?.job_time) return null;

    const url = bookingUrl(entry.buildingId, entry.doorId);

    return (
        <div className="remedial-door-row">
            <Link
                to={`/book-remedial/${entry.buildingId}/${entry.doorId}`}
                style={{ textDecoration: "none", color: "inherit" }}
            >
                <div className="remedial-door-title">{doorLabel(doorType, door)}</div>
                <div className="remedial-door-sub">
                    {door.workNeeded || "No work-needed notes."}
                </div>
            </Link>
            <span className="rm-badge rm-badge-amber">{toTitleCase(remedialInfo.job_time)} Job</span>
            <button
                type="button"
                className="btn-remedial-outline"
                onClick={(e) => { e.preventDefault(); copyToClipboard(url); }}
                title="Copy booking link"
            >
                <FontAwesomeIcon icon={faCopy} size="xs" />
                Copy Link
            </button>
            <Link to={`/book-remedial/${entry.buildingId}/${entry.doorId}`} className="btn-remedial-primary">
                <FontAwesomeIcon icon={faHammer} size="xs" />
                Book
            </Link>
        </div>
    );
};

const QueueBuildingGroup = ({ group, isOpen, onToggle, onBuildingName, buildingNames }) => (
    <div className="remedial-card">
        <div className="remedial-card-header" onClick={() => onToggle(group.buildingId)}>
            <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <FontAwesomeIcon icon={faBuilding} style={{ color: "#dc3545", flexShrink: 0 }} />
                <span style={{
                    fontWeight: 700, fontSize: "0.95rem", color: "#111827",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                    {buildingNames[group.buildingId] || "Loading building…"}
                </span>
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <span className="rm-badge rm-badge-red">
                    {group.entries.length} door{group.entries.length === 1 ? "" : "s"}
                </span>
                <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} size="xs" style={{ color: "#9ca3af" }} />
            </span>
        </div>

        {isOpen && (
            <div>
                {group.entries.map((entry) => (
                    <QueueDoorRow key={`${entry.buildingId}-${entry.doorId}`} entry={entry} onBuildingName={onBuildingName} />
                ))}
            </div>
        )}
    </div>
);

const DoorsReadyTab = ({ groupedByBuilding, loading, hasError, errorMessage, onRetry }) => {
    const [openBuildings, setOpenBuildings] = useState({});
    const [buildingNames, setBuildingNames] = useState({});
    const toggleBuilding = (id) => setOpenBuildings((o) => ({ ...o, [id]: !o[id] }));
    const handleBuildingName = (id, name) => setBuildingNames((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }));

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" style={{ color: "#dc3545", width: "2.5rem", height: "2.5rem" }} role="status" />
                <p className="mt-3" style={{ color: "#6b7280" }}>Loading doors...</p>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="remedials-empty">
                <FontAwesomeIcon icon={faExclamationTriangle} className="remedials-empty-icon" style={{ color: "#dc2626" }} />
                <p style={{ color: "#dc2626", fontWeight: 700, marginBottom: 4 }}>Couldn't load doors right now.</p>
                <p style={{ fontSize: "0.85rem", color: "#9ca3af", marginBottom: 18 }}>
                    {errorMessage || "The connection timed out. Please try again."}
                </p>
                <button type="button" className="btn-remedial-primary" onClick={onRetry}>
                    <FontAwesomeIcon icon={faRotateRight} />
                    Retry
                </button>
            </div>
        );
    }

    if (groupedByBuilding.length === 0) {
        return (
            <div className="remedials-empty">
                <FontAwesomeIcon icon={faHammer} className="remedials-empty-icon" />
                <p>No doors are currently ready to have remedial work booked.</p>
            </div>
        );
    }

    return (
        <div>
            {groupedByBuilding.map((group) => (
                <QueueBuildingGroup
                    key={group.buildingId}
                    group={group}
                    isOpen={openBuildings[group.buildingId] !== false}
                    onToggle={toggleBuilding}
                    onBuildingName={handleBuildingName}
                    buildingNames={buildingNames}
                />
            ))}
        </div>
    );
};

const CARPENTER_TABLE_HEADINGS = [
    "#", "Door", "Building", "Date", "Job Time", "Contact", "Notes", "Booked At", "Action",
];

const CarpenterBookingsTab = ({ carpenters, bookings, loading, hasError, errorMessage, onRetry }) => {
    const [activeCarpenterId, setActiveCarpenterId] = useState(null);
    const [dateFilter, setDateFilter] = useState("future");

    const bookingsByCarpenter = useMemo(() => {
        const map = new Map();
        carpenters.forEach((c) => map.set(c.uid, { carpenter: c, bookings: [] }));

        bookings.forEach((b) => {
            if (!map.has(b.carpenter_id)) {
                map.set(b.carpenter_id, {
                    carpenter: { uid: b.carpenter_id, name: b.carpenter_name },
                    bookings: [],
                });
            }
            map.get(b.carpenter_id).bookings.push(b);
        });

        return [...map.values()].sort((a, b) =>
            (a.carpenter.name || a.carpenter.email || "").localeCompare(b.carpenter.name || b.carpenter.email || "")
        );
    }, [carpenters, bookings]);

    useEffect(() => {
        if (bookingsByCarpenter.length === 0) { setActiveCarpenterId(null); return; }
        if (!activeCarpenterId || !bookingsByCarpenter.some((g) => g.carpenter.uid === activeCarpenterId)) {
            setActiveCarpenterId(bookingsByCarpenter[0].carpenter.uid);
        }
    }, [bookingsByCarpenter]);

    const todayId = useBusinessTodayId();
    const activeGroup = bookingsByCarpenter.find((g) => g.carpenter.uid === activeCarpenterId) || bookingsByCarpenter[0] || null;

    const filteredBookings = useMemo(() => {
        if (!activeGroup) return [];
        let list = [...activeGroup.bookings];
        if (dateFilter === "future") list = list.filter((b) => b.date >= todayId);
        if (dateFilter === "past") list = list.filter((b) => b.date < todayId);
        return list.sort((a, b) => a.date.localeCompare(b.date));
    }, [activeGroup, dateFilter, todayId]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" style={{ color: "#dc3545", width: "2.5rem", height: "2.5rem" }} role="status" />
                <p className="mt-3" style={{ color: "#6b7280" }}>Loading carpenter bookings...</p>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="remedials-empty">
                <FontAwesomeIcon icon={faExclamationTriangle} className="remedials-empty-icon" style={{ color: "#dc2626" }} />
                <p style={{ color: "#dc2626", fontWeight: 700, marginBottom: 4 }}>Couldn't load bookings.</p>
                <p style={{ fontSize: "0.85rem", color: "#9ca3af", marginBottom: 18 }}>{errorMessage}</p>
                <button type="button" className="btn-remedial-primary" onClick={onRetry}>
                    <FontAwesomeIcon icon={faRotateRight} />
                    Retry
                </button>
            </div>
        );
    }

    if (bookingsByCarpenter.length === 0) {
        return (
            <div className="remedials-empty">
                <FontAwesomeIcon icon={faUser} className="remedials-empty-icon" />
                <p>No carpenters found.</p>
            </div>
        );
    }

    const handleCancel = async (booking) => {
        if (!window.confirm(`Cancel ${booking.carpenter_name}'s ${booking.job_time.toLowerCase()} booking for ${booking.door_number} on ${fmtDateId(booking.date)}?`)) return;
        try {
            await cancelRemedialBookingTx(booking);
            toast.success("Booking cancelled.");
        } catch (err) {
            console.error("Failed to cancel booking:", err);
            toast.error(err.message || "Could not cancel booking.");
        }
    };

    const futureCountFor = (group) => group.bookings.filter((b) => b.date >= todayId).length;

    return (
        <div>
            <Nav
                variant="tabs"
                className="mb-4 remedials-tabs"
                activeKey={activeGroup?.carpenter.uid}
                onSelect={setActiveCarpenterId}
            >
                {bookingsByCarpenter.map((group) => (
                    <Nav.Item key={group.carpenter.uid}>
                        <Nav.Link eventKey={group.carpenter.uid}>
                            <FontAwesomeIcon icon={faUser} className="me-2" />
                            {group.carpenter.name || group.carpenter.email || "Unknown"}
                            <span className="rm-badge rm-badge-red" style={{ marginLeft: 8 }}>
                                {futureCountFor(group)}
                            </span>
                        </Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            <div className="rm-toolbar-row">
                <div style={{ minWidth: 0 }}>
                    <h4 style={{
                        margin: 0, fontWeight: 700, color: "#0f172a", fontSize: "1.1rem",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                        {activeGroup?.carpenter.name || activeGroup?.carpenter.email || "Unknown"}
                    </h4>
                    <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem" }}>
                        {filteredBookings.length} booking{filteredBookings.length !== 1 ? "s" : ""}
                        {dateFilter !== "all" && <span> ({dateFilter === "future" ? "upcoming" : "past"})</span>}
                    </p>
                </div>
                <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="rm-form-input"
                    style={{ maxWidth: 200, fontWeight: 500, cursor: "pointer" }}
                >
                    <option value="future">Upcoming</option>
                    <option value="past">Past</option>
                    <option value="all">All Dates</option>
                </select>
            </div>

            {filteredBookings.length === 0 ? (
                <div className="text-center py-5">
                    <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
                    <p style={{ color: "#9ca3af", fontSize: "0.95rem" }}>No bookings found.</p>
                </div>
            ) : (
                <div className="rm-table-wrap">
                    <table className="table align-middle mb-0 rm-responsive-table" style={{ fontSize: "0.84rem" }}>
                        <thead style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                            <tr>
                                {CARPENTER_TABLE_HEADINGS.map((heading) => (
                                    <th
                                        key={heading}
                                        className={heading === "#" || heading === "Date" || heading === "Job Time" || heading === "Action" ? "text-center" : ""}
                                        style={{
                                            fontWeight: 600, fontSize: "0.73rem", textTransform: "uppercase",
                                            letterSpacing: "0.5px", color: "#64748b", padding: "14px 16px",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {heading}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBookings.map((b, idx) => {
                                const rowBg = idx % 2 === 0 ? "#ffffff" : "#f8fafc";
                                const isPast = b.date < todayId;
                                return (
                                    <tr key={b.id} style={{ backgroundColor: rowBg }}>
                                        <td data-label="#" className="text-center" style={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.8rem", padding: "12px 16px" }}>
                                            {String(idx + 1).padStart(2, "0")}
                                        </td>
                                        <td data-label="Door" style={{ fontWeight: 700, color: "#111827", padding: "12px 16px" }}>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                <span>{b.door_number}</span>
                                                <Link to={`/book-remedial/${b.building_id}/${b.door_id}`} className="rm-link" style={{ fontSize: "0.72rem" }}>
                                                    <FontAwesomeIcon icon={faDoorClosed} size="xs" />
                                                    View door
                                                </Link>
                                            </div>
                                        </td>
                                        <td data-label="Building" style={{ padding: "12px 16px" }}>
                                            <a href={`/doors/${b.building_id}`} target="_blank" rel="noopener noreferrer" className="rm-link">
                                                <FontAwesomeIcon icon={faBuilding} size="xs" />
                                                {b.building_name || "—"}
                                            </a>
                                        </td>
                                        <td data-label="Date" className="text-center" style={{ padding: "12px 16px" }}>
                                            <span className="badge" style={{
                                                backgroundColor: isPast ? "#f3f4f6" : "#ecfdf5",
                                                color: isPast ? "#6b7280" : "#065f46",
                                                fontWeight: 600, fontSize: "0.73rem",
                                                padding: "6px 12px", borderRadius: "20px",
                                            }}>
                                                {fmtDateId(b.date)}
                                            </span>
                                        </td>
                                        <td data-label="Job Time" className="text-center" style={{ padding: "12px 16px" }}>
                                            <span className="badge" style={{
                                                backgroundColor: "#fffbeb", color: "#92400e",
                                                fontWeight: 600, fontSize: "0.73rem",
                                                padding: "6px 12px", borderRadius: "20px",
                                            }}>
                                                {b.job_time}
                                            </span>
                                            {b.booking_slot && b.booking_slot !== BOOKING_SLOT.FULL_DAY && (
                                                <div style={{ marginTop: 4, fontSize: "0.7rem", color: "#64748b", fontWeight: 600 }}>
                                                    {b.booking_slot}
                                                </div>
                                            )}
                                        </td>
                                        <td data-label="Contact" style={{ padding: "12px 16px" }}>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                {b.created_by_name && (
                                                    <span style={{ fontWeight: 600, color: "#374151", fontSize: "0.8rem" }}>
                                                        {b.created_by_name}
                                                    </span>
                                                )}
                                                {b.contact_email && (
                                                    <a href={`mailto:${b.contact_email}`} style={{ color: "#3b82f6", fontSize: "0.78rem", textDecoration: "none" }}>
                                                        {b.contact_email}
                                                    </a>
                                                )}
                                                {b.contact_phone && (
                                                    <a href={`tel:${b.contact_phone}`} style={{ color: "#6b7280", fontSize: "0.78rem", textDecoration: "none" }}>
                                                        {b.contact_phone}
                                                    </a>
                                                )}
                                                {!b.created_by_name && !b.contact_email && !b.contact_phone && (
                                                    <span style={{ color: "#cbd5e1" }}>—</span>
                                                )}
                                            </div>
                                        </td>
                                        <td data-label="Notes" style={{ padding: "12px 16px", color: "#6b7280", fontSize: "0.8rem", maxWidth: 200 }}>
                                            {b.notes || <span style={{ color: "#cbd5e1" }}>—</span>}
                                        </td>
                                        <td data-label="Booked At" style={{ padding: "12px 16px", color: "#6b7280", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                                            {b.created_at ? fmtTimestamp(b.created_at) : "—"}
                                        </td>
                                        <td data-label="Action" className="text-center" style={{ padding: "12px 16px" }}>
                                            <button type="button" className="btn-remedial-danger-outline" onClick={() => handleCancel(b)}>
                                                <FontAwesomeIcon icon={faBan} size="xs" />
                                                Cancel
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const OverviewPage = ({ isAdmin, roleLoaded }) => {
    const [tab, setTab] = useState("book");
    const [refreshKey, setRefreshKey] = useState(0);

    const { groupedByBuilding, loading: doorsLoading, error: doorsError } = useBookRemedialsQueue(refreshKey);
    const { carpenters, loading: carpentersLoading, error: carpentersError } = useCarpenters(refreshKey);
    const { bookings, loading: bookingsLoading, error: bookingsError } = useActiveCarpenterBookings(refreshKey);

    if (!roleLoaded) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" style={{ color: "#dc3545", width: "2.5rem", height: "2.5rem" }} role="status" />
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="remedials-empty">
                <FontAwesomeIcon icon={faLock} className="remedials-empty-icon" />
                <p style={{ fontWeight: 700, color: "#374151" }}>This overview is for admins only.</p>
                <p style={{ fontSize: "0.88rem" }}>
                    You need to book by the building link given to you.
                </p>
            </div>
        );
    }

    return (
        <>
            <Nav variant="tabs" className="mb-4 remedials-tabs" activeKey={tab} onSelect={setTab}>
                <Nav.Item>
                    <Nav.Link eventKey="book">
                        <FontAwesomeIcon icon={faHammer} className="me-2" />
                        Doors Ready to Book
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="carpenters">
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        Carpenter Bookings
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {tab === "book" && (
                <DoorsReadyTab
                    groupedByBuilding={groupedByBuilding}
                    loading={doorsLoading}
                    hasError={Boolean(doorsError)}
                    errorMessage={doorsError}
                    onRetry={() => setRefreshKey((k) => k + 1)}
                />
            )}

            {tab === "carpenters" && (
                <CarpenterBookingsTab
                    carpenters={carpenters}
                    bookings={bookings}
                    loading={bookingsLoading || carpentersLoading}
                    hasError={Boolean(bookingsError || carpentersError)}
                    errorMessage={bookingsError || carpentersError}
                    onRetry={() => setRefreshKey((k) => k + 1)}
                />
            )}
        </>
    );
};

const DoorBookingPage = ({ buildingId, doorId, user }) => {
    const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);
    const { door, doorType, loading, notFound, error: doorError } = useDoorByPath(buildingId, doorId, availabilityRefreshKey);
    const { info: remedialInfo, loading: infoLoading, error: infoError } = useLatestRemedialInfo(buildingId, doorId, doorType, availabilityRefreshKey);
    const buildingName = useBuildingName(buildingId);
    const { carpenters, loading: carpentersLoading, error: carpentersError } = useCarpenters(availabilityRefreshKey);
    const todayId = useBusinessTodayId();

    const allWorkingDays = useMemo(() => getBookingCalendarDays(CALENDAR_MONTHS, todayId), [todayId]);
    const allDateIds = useMemo(() => allWorkingDays.map(toDateId), [allWorkingDays]);
    const dateIds = allDateIds;
    const calendarMonths = useMemo(() => {
        const today = dateFromId(todayId);
        today.setHours(0, 0, 0, 0);
        return Array.from({ length: CALENDAR_MONTHS }, (_, index) => {
            const date = new Date(today.getFullYear(), today.getMonth() + index, 1);
            return {
                year: date.getFullYear(),
                month: date.getMonth(),
                key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
                label: date.toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
            };
        });
    }, [todayId]);
    const calendarPageCount = calendarMonths.length;
    const [calendarPage, setCalendarPage] = useState(0);
    const activeCalendarMonth = calendarMonths[calendarPage] || calendarMonths[0];
    const visibleDateIds = useMemo(
        () => dateIds.filter((dateId) => dateId.startsWith(activeCalendarMonth?.key || "")),
        [dateIds, activeCalendarMonth]
    );
    const leadingBlankCount = useMemo(() => {
        if (!activeCalendarMonth) return 0;
        const firstDay = new Date(activeCalendarMonth.year, activeCalendarMonth.month, 1).getDay();
        return firstDay === 0 ? 0 : firstDay - 1;
    }, [activeCalendarMonth]);
    const visibleCalendarCells = useMemo(() => {
        const cells = [...Array(leadingBlankCount).fill(null), ...visibleDateIds];
        while (cells.length % 6 !== 0) cells.push(null);
        return cells;
    }, [leadingBlankCount, visibleDateIds]);
    const visibleWeekCount = Math.max(1, Math.ceil(visibleCalendarCells.length / 6));
    const visibleRangeLabel = activeCalendarMonth?.label || "";

    const { capacityByKey, loading: capacityLoading, error: capacityError } = useCapacityForDates(allDateIds, availabilityRefreshKey);
    const { bookingsByKey, loading: bookingsLoading, error: bookingsWindowError } = useBookingsForDates(allDateIds, availabilityRefreshKey);
    const availabilityError = doorError || capacityError || bookingsWindowError || carpentersError || infoError;

    const relevantBuildingIds = useMemo(() => {
        const ids = new Set([buildingId]);
        Object.values(bookingsByKey).forEach((list) => list.forEach((b) => {
            if (b.building_id) ids.add(b.building_id);
        }));
        Object.values(capacityByKey).forEach((capacity) => {
            if (capacity?.morning_building_id) ids.add(capacity.morning_building_id);
            if (capacity?.afternoon_building_id) ids.add(capacity.afternoon_building_id);
        });
        return [...ids];
    }, [bookingsByKey, capacityByKey, buildingId]);
    const buildingDocsById = useBuildingDocs(relevantBuildingIds, availabilityRefreshKey);

    const [selection, setSelection] = useState(null);
    const [notes, setNotes] = useState("");
    const [bookedByName, setBookedByName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [saving, setSaving] = useState(false);

    const [feasibilityByKey, setFeasibilityByKey] = useState({});

    const carpenterLoadById = useMemo(
        () => buildCarpenterLoadById(carpenters, bookingsByKey),
        [carpenters, bookingsByKey]
    );

    const jobTime = remedialInfo?.job_time || null;

    useEffect(() => {
        setFeasibilityByKey({});
    }, [buildingId]);

    useEffect(() => {
        if (!jobTime || jobTime !== "Half day" || bookingsLoading || capacityLoading || availabilityError) return;

        carpenters.forEach((c) => {
            allWorkingDays.forEach((d) => {
                const dateId = toDateId(d);
                const key = `${c.uid}_${dateId}`;
                const capacity = capacityByKey[key] || {};
                const existing = bookingsByKey[key] || [];
                const state = getCarpenterDayState(capacity, existing);

                if (state.usedCount !== 1) return;
                const requestedSlot = state.morningUsed ? BOOKING_SLOT.AFTERNOON : BOOKING_SLOT.MORNING;
                const otherBuildingId = state.morningUsed ? state.morningBuildingId : state.afternoonBuildingId;
                if (!otherBuildingId || otherBuildingId === buildingId) return;

                const route = pairingRouteForSlot(requestedSlot, buildingId, otherBuildingId);
                const feasibilityKey = routeFeasibilityKey(
                    c.uid,
                    dateId,
                    requestedSlot,
                    route.fromBuildingId,
                    route.toBuildingId
                );
                if (feasibilityByKey[feasibilityKey] !== undefined) return;

                const fromBuildingData = buildingDocsById[route.fromBuildingId];
                const toBuildingData = buildingDocsById[route.toBuildingId];

                if (fromBuildingData === undefined || toBuildingData === undefined) return;
                if (!fromBuildingData || !toBuildingData) {
                    setFeasibilityByKey((prev) => ({
                        ...prev,
                        [feasibilityKey]: { status: "checking-error", minutes: null },
                    }));
                    return;
                }

                setFeasibilityByKey((prev) => ({
                    ...prev,
                    [feasibilityKey]: { status: "checking", minutes: null },
                }));

                getTravelMinutesBetweenBuildings(
                    route.fromBuildingId,
                    fromBuildingData,
                    route.toBuildingId,
                    toBuildingData
                ).then((minutes) => {
                    setFeasibilityByKey((prev) => ({
                        ...prev,
                        [feasibilityKey]: {
                            status: typeof minutes !== "number"
                                ? "checking-error"
                                : minutes <= MAX_TRAVEL_MINUTES
                                    ? "near"
                                    : "unavailable",
                            minutes,
                        },
                    }));
                });
            });
        });
    }, [jobTime, allWorkingDays, carpenters, capacityByKey, bookingsByKey, buildingDocsById, bookingsLoading, capacityLoading, buildingId, feasibilityByKey, availabilityError]);

    const halfDaySuitability = useMemo(() => {
        if (jobTime !== "Half day" || carpenters.length === 0) {
            return {
                bySlotKey: {},
                rankedSlots: [],
                featuredSlotKeys: [],
                recommendedSlotKey: null,
                goodSlotKeys: [],
                pendingRouteChecks: 0,
                ready: true,
            };
        }

        return buildPrecalculatedHalfDaySuitability({
            carpenters,
            dateIds: allDateIds,
            todayId,
            capacityByKey,
            bookingsByKey,
            buildingId,
            feasibilityByKey,
            carpenterLoadById,
        });
    }, [
        jobTime,
        carpenters,
        allDateIds,
        todayId,
        capacityByKey,
        bookingsByKey,
        buildingId,
        feasibilityByKey,
        carpenterLoadById,
    ]);

    const fullDaySuitability = useMemo(() => {
        if (jobTime !== "Full day" || carpenters.length === 0) {
            return { byDateId: {}, featuredDateIds: [] };
        }

        return buildPrecalculatedFullDaySuitability({
            carpenters,
            dateIds: allDateIds,
            todayId,
            capacityByKey,
            bookingsByKey,
            carpenterLoadById,
        });
    }, [
        jobTime,
        carpenters,
        allDateIds,
        todayId,
        capacityByKey,
        bookingsByKey,
        carpenterLoadById,
    ]);

    useEffect(() => {
        if (!selection || saving || capacityLoading || bookingsLoading || !jobTime || carpenters.length === 0) return;

        const availability = jobTime === "Full day"
            ? fullDaySuitability.byDateId[selection.dateId]
            : halfDaySuitability.bySlotKey[`${selection.dateId}_${selection.bookingSlot}`];

        if (!availability?.canBook) {
            setSelection(null);
            toast.info(
                availability?.checking
                    ? "Availability is being rechecked. Please choose the slot again when the live check finishes."
                    : "That slot has just become unavailable. The calendar has been updated live."
            );
        }
    }, [selection, saving, jobTime, carpenters, capacityByKey, bookingsByKey, feasibilityByKey, capacityLoading, bookingsLoading, buildingId, halfDaySuitability, fullDaySuitability]);

    if (loading || infoLoading || bookingsLoading || capacityLoading || carpentersLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" style={{ color: "#dc3545", width: "2.5rem", height: "2.5rem" }} role="status" />
                <p className="mt-3" style={{ color: "#6b7280" }}>Loading door...</p>
            </div>
        );
    }

    if (availabilityError) {
        return (
            <div className="remedial-card" style={{ padding: 30, textAlign: "center" }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: "2rem", color: "#dc2626", marginBottom: 12 }} />
                <h5 style={{ fontWeight: 700, color: "#111827" }}>Booking page temporarily unavailable</h5>
                <p style={{ color: "#6b7280", fontSize: "0.9rem", marginBottom: 16 }}>
                    We couldn't safely verify the door and live carpenter capacity. No slots are being shown until the live data reconnects.
                </p>
                <button type="button" className="btn-remedial-primary" onClick={() => setAvailabilityRefreshKey((key) => key + 1)}>
                    <FontAwesomeIcon icon={faRotateRight} /> Retry availability
                </button>
            </div>
        );
    }

    if (notFound || !door) {
        return (
            <div className="remedials-empty">
                <FontAwesomeIcon icon={faExclamationTriangle} className="remedials-empty-icon" style={{ color: "#dc2626" }} />
                <p style={{ fontWeight: 700, color: "#dc2626" }}>Door not found.</p>
                <p style={{ fontSize: "0.85rem" }}>Double-check the link you were given.</p>
            </div>
        );
    }

    const doorNumber = doorLabel(doorType, door);
    const alreadyDone = Boolean(door.workDone) || Boolean(door.workDoneUpdatedAt);
    const existingBooking = door.remedial_booking || null;

    if (alreadyDone) {
        return (
            <div className="remedial-card" style={{ padding: 30, textAlign: "center" }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: "2rem", color: "#059669", marginBottom: 12 }} />
                <h5 style={{ fontWeight: 700, color: "#111827" }}>{doorNumber} · {buildingName}</h5>
                <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>
                    The carpentry work on this door has already been marked as done — no booking is needed.
                </p>
            </div>
        );
    }

    if (existingBooking) {
        return (
            <div className="remedial-card" style={{ padding: 30, textAlign: "center" }}>
                <FontAwesomeIcon icon={faHammer} style={{ fontSize: "2rem", color: "#dc3545", marginBottom: 12 }} />
                <h5 style={{ fontWeight: 700, color: "#111827" }}>{doorNumber} · {buildingName}</h5>
                <p style={{ color: "#6b7280", fontSize: "0.9rem", marginBottom: 4 }}>
                    This door already has a carpenter booked.
                </p>
                <div style={{ marginTop: 10 }}>
                    <span className="rm-badge rm-badge-amber">
                        {fmtDateId(existingBooking.date)} · {existingBooking.booking_slot || existingBooking.job_time}
                    </span>
                </div>
            </div>
        );
    }

    if (!jobTime) {
        return (
            <div className="remedial-card" style={{ padding: 30, textAlign: "center" }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: "2rem", color: "#d97706", marginBottom: 12 }} />
                <h5 style={{ fontWeight: 700, color: "#111827" }}>{doorNumber} · {buildingName || "This door"}</h5>
                <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>
                    This door hasn't been scoped for remedial work yet. The remedial team needs to add
                    job details (half day / full day) before a carpenter can be booked.
                </p>
            </div>
        );
    }

    const getHalfDayAvailability = (dateId, requestedSlot) => {
        const slotKey = `${dateId}_${requestedSlot}`;
        if (halfDaySuitability.bySlotKey[slotKey]) return halfDaySuitability.bySlotKey[slotKey];

        return getLiveHalfDayAvailability({
            carpenters,
            dateId,
            requestedSlot,
            capacityByKey,
            bookingsByKey,
            buildingId,
            feasibilityByKey,
            carpenterLoadById,
        });
    };

    const getFullDayAvailability = (dateId) => fullDaySuitability.byDateId[dateId] || getLiveFullDayAvailability({
        carpenters,
        dateId,
        capacityByKey,
        bookingsByKey,
        carpenterLoadById,
    });

    const selectPublicSlot = (dateId, bookingSlot, availability) => {
        if (!availability?.canBook) return;
        setSelection({ dateId, bookingSlot });
    };

    const resolveCarpenterCandidatesForSubmit = async (dateId, bookingSlot) => {
        const availability = jobTime === "Full day"
            ? getFullDayAvailability(dateId)
            : getHalfDayAvailability(dateId, bookingSlot);

        return availability.candidates;
    };

    const isRetryableAssignmentError = (message = "") => /no longer available|morning slot|afternoon slot|enough availability|only available|matched safely|route must be rechecked|within 30 minutes/i.test(message);

    const handleConfirm = async () => {
        if (!selection) return;
        if (!bookedByName.trim()) {
            toast.warning("Please enter your name so we know who booked this.");
            return;
        }
        if (!contactEmail.trim() || !isValidEmail(contactEmail)) {
            toast.warning("Please enter a valid email address.");
            return;
        }
        if (!contactPhone.trim() || !isValidPhone(contactPhone)) {
            toast.warning("Please enter a valid phone number.");
            return;
        }

        // if (jobTime === "Half day") {
        //     const key = `${selection.carpenterId}_${selection.dateId}`;
        //     const existing = (bookingsByKey[key] || []).filter((b) => b.job_time === "Half day");
        //     // if (existing.length === 1 && existing[0].building_id !== buildingId) {
        //     if (existing.length === 1) {
        //         const otherBuildingData = buildingDocsById[existing[0].building_id];
        //         const thisBuildingData = buildingDocsById[buildingId];
        //         // if (otherBuildingData && thisBuildingData) {
        //         //     const minutes = await getTravelMinutesBetweenBuildings(
        //         //         buildingId, thisBuildingData, existing[0].building_id, otherBuildingData
        //         //     );
        //         //     if (minutes !== null && minutes > MAX_TRAVEL_MINUTES) {
        //         //         toast.error("This carpenter's other job that day is too far away. Please pick another slot.");
        //         //         return;
        //         //     }
        //         // }

        //         if (otherBuildingData && thisBuildingData) {
        //             const minutes = 999;
        //             if (minutes !== null && minutes > MAX_TRAVEL_MINUTES) {
        //                 toast.error("This carpenter's other job that day is too far away. Please pick another slot.");
        //                 return;
        //             }
        //         }
        //     }
        // }

        const { start, end } = getRemedialSlotTimes(jobTime, selection.dateId, selection.bookingSlot);
        const thisBuildingData = buildingDocsById[buildingId] || {};
        const { address: buildingAddress, city: buildingCity } = buildingAddressParts(thisBuildingData);
        const calendarEventBase = {
            title: `${doorNumber} · ${buildingName || "Remedial"} (${jobTime} · ${selection.bookingSlot})`,
            description: notes?.trim() || "",
            type: "remedial",
            category: "remedial",
            start,
            end,
            buildingEmail: "",
            buildingAddress: buildingAddress || "",
            buildingCity: buildingCity || "",
            travelDistanceMiles: 0,
            travelTimeMinutes: 0,
            surveyTimeMinutes: 0,
            totalCommunalDoors: 0,
            totalResiDoors: 0,
            preventRescheduling: false,
            inspectorName: null,
            directorName: null,
            lunchDuration: "",
            postcode: "",
            assignedInspector: null,
            assignedDirector: null,
            doorUrl: null,
            materialCost: "",
            jobPrice: "",
            labourCost: "",
            ticket_id: null,
        };

        setSaving(true);
        try {
            const assignmentCandidates = await resolveCarpenterCandidatesForSubmit(
                selection.dateId,
                selection.bookingSlot
            );

            if (assignmentCandidates.length === 0) {
                toast.error("That slot is no longer available. The live calendar has been updated — please choose another date or time.");
                return;
            }

            let assignedCarpenter = null;
            let lastAssignmentError = null;

            for (const assignment of assignmentCandidates) {
                try {
                    await bookRemedialTx({
                        buildingId,
                        doorType,
                        doorId,
                        doorNumber,
                        buildingName,
                        jobTime,
                        bookingSlot: selection.bookingSlot,
                        carpenter: assignment.carpenter,
                        dateId: selection.dateId,
                        notes,
                        actorUid: user?.uid || null,
                        bookedByName: bookedByName.trim(),
                        contactEmail,
                        contactPhone,
                        calendarEventBase,
                    });
                    assignedCarpenter = assignment.carpenter;
                    break;
                } catch (assignmentErr) {
                    lastAssignmentError = assignmentErr;
                    if (!isRetryableAssignmentError(assignmentErr?.message || "")) throw assignmentErr;
                }
            }

            if (!assignedCarpenter) {
                throw lastAssignmentError || new Error("That slot has just become unavailable. Please choose another slot.");
            }

            toast.success("Booking confirmed!");

            setSelection(null);
            setNotes("");
        } catch (err) {
            console.error("Failed to book remedial:", err);
            toast.error(err.message || "Could not confirm this booking.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="remedial-card" style={{ padding: "18px 22px" }}>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 8 }}>
                    Work Required
                </div>
                {door.workNeeded ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                        {door.workNeeded
                            .split(/\||\n/)
                            .map((item) => item.trim())
                            .filter(Boolean)
                            .map((item, idx) => (
                                <span
                                    key={idx}
                                    style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: "0.85rem", color: "#374151" }}
                                >
                                    <span style={{ color: "#dc3545", lineHeight: 1.4 }}>•</span>
                                    <span>{item}</span>
                                </span>
                            ))}
                    </div>
                ) : (
                    <p style={{ margin: 0, fontSize: "0.82rem", color: "#6b7280" }}>
                        No work-needed notes recorded.
                    </p>
                )}
            </div>

            <div className="remedial-card" style={{ padding: "16px 22px" }}>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "12px 20px",
                    marginBottom: 16,
                }}>
                    <div>
                        <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 2 }}>
                            Building
                        </div>
                        <div style={{ fontSize: "0.86rem", fontWeight: 600, color: "#111827" }}>
                            {buildingName || "—"}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 2 }}>
                            Flat / Door No.
                        </div>
                        <div style={{ fontSize: "0.86rem", fontWeight: 600, color: "#111827" }}>
                            {doorNumber}
                        </div>
                    </div>
                </div>

                <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0 16px" }} />

                <label className="rm-form-label" style={{ marginBottom: 12, fontSize: "0.84rem" }}>
                    <FontAwesomeIcon icon={faUser} className="rm-label-icon" />
                    Your Contact Details
                </label>

                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 12,
                }}>
                    <div>
                        <label className="rm-form-label" style={{ fontSize: "0.76rem", marginBottom: 5, fontWeight: 600 }}>
                            Name <span style={{ color: "#dc2626" }}>*</span>
                        </label>
                        <input
                            type="text"
                            className="rm-form-input"
                            placeholder="Your name"
                            value={bookedByName}
                            onChange={(e) => setBookedByName(e.target.value)}
                            style={{ padding: "9px 12px", fontSize: "0.85rem" }}
                        />
                    </div>
                    <div>
                        <label className="rm-form-label" style={{ fontSize: "0.76rem", marginBottom: 5, fontWeight: 600 }}>
                            Email <span style={{ color: "#dc2626" }}>*</span>
                        </label>
                        <input
                            type="email"
                            className="rm-form-input"
                            placeholder="you@example.com"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            style={{ padding: "9px 12px", fontSize: "0.85rem" }}
                        />
                    </div>
                    <div>
                        <label className="rm-form-label" style={{ fontSize: "0.76rem", marginBottom: 5, fontWeight: 600 }}>
                            Phone <span style={{ color: "#dc2626" }}>*</span>
                        </label>
                        <input
                            type="tel"
                            className="rm-form-input"
                            placeholder="+44 7700 900123"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            style={{ padding: "9px 12px", fontSize: "0.85rem" }}
                        />
                    </div>
                </div>
            </div>

            <div className="remedial-card" style={{ padding: "22px 24px" }}>
                <div style={{
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    gap: 14, flexWrap: "wrap", marginBottom: 16,
                }}>
                    <div style={{ minWidth: 0 }}>
                        <div style={{
                            display: "flex", alignItems: "center", gap: 9,
                            fontSize: "1rem", fontWeight: 800, color: "#0f172a", marginBottom: 4,
                        }}>
                            <span style={{
                                width: 34, height: 34, borderRadius: 10, display: "inline-flex",
                                alignItems: "center", justifyContent: "center", background: "#fef2f2",
                                color: "#dc3545", flexShrink: 0,
                            }}>
                                <FontAwesomeIcon icon={faCalendarDay} />
                            </span>
                            Preferred Date &amp; Time
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#64748b", lineHeight: 1.5, paddingLeft: 43 }}>
                            Choose from the available calendar slots below. Saturdays are included.
                        </div>
                    </div>
                    <div className="rm-booking-live-meta">
                        <span className="rm-live-availability" title="Updates automatically when another user books or cancels a slot.">
                            <span className="rm-live-dot" aria-hidden="true" />
                            Live availability
                        </span>
                        <span className="rm-badge rm-badge-amber" style={{ whiteSpace: "nowrap" }}>
                            {toTitleCase(jobTime)} Job
                        </span>
                    </div>
                </div>

                {jobTime === "Half day" && (
                    <div className="rm-smart-choice-guide">
                        <div className="rm-smart-choice-guide-title">Smart choices</div>
                        <div className="rm-smart-choice-guide-text">
                            <strong>Recommended</strong> is the best route for this building. <strong>Good option</strong> choices are also fully bookable. Other available slots can still be selected.
                        </div>
                        {/* <div className="rm-smart-choice-guide-note">
                            Carpenter assignment happens automatically on confirmation: same building first, then nearby within {MAX_TRAVEL_MINUTES} minutes, then the least-loaded free carpenter.
                        </div> */}
                    </div>
                )}

                {capacityLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" style={{ color: "#dc3545" }} />
                    </div>
                ) : carpenters.length === 0 ? (
                    <p style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
                        No booking availability is currently configured. Please contact the remedial team.
                    </p>
                ) : (
                    <>
                        <div className="rm-air-calendar-toolbar">
                            <button
                                type="button"
                                className="btn-remedial-outline rm-calendar-nav-btn"
                                onClick={() => setCalendarPage((page) => Math.max(0, page - 1))}
                                disabled={calendarPage === 0}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} size="xs" />
                                <span className="rm-calendar-nav-text">Previous</span>
                            </button>

                            <div className="rm-air-calendar-range" aria-live="polite">
                                <span className="rm-air-calendar-range-label">{visibleRangeLabel}</span>
                                <span className="rm-air-calendar-page-label">
                                    Month {calendarPage + 1} of {calendarPageCount}
                                </span>
                            </div>

                            <button
                                type="button"
                                className="btn-remedial-outline rm-calendar-nav-btn"
                                onClick={() => setCalendarPage((page) => Math.min(calendarPageCount - 1, page + 1))}
                                disabled={calendarPage >= calendarPageCount - 1}
                            >
                                <span className="rm-calendar-nav-text">Next</span>
                                <FontAwesomeIcon icon={faChevronRight} size="xs" />
                            </button>
                        </div>

                        <div className="rm-air-calendar-wrap">
                            <div className="rm-air-calendar">
                                <div className="rm-air-weekday-row" aria-hidden="true">
                                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
                                        <div key={label} className={`rm-air-weekday ${label === "Sat" ? "is-saturday" : ""}`}>
                                            {label}
                                        </div>
                                    ))}
                                </div>

                                <div className="rm-air-weeks">
                                    {Array.from({ length: visibleWeekCount }, (_, localWeekIndex) => {
                                        const weekIndex = localWeekIndex;
                                        const weekDateIds = visibleCalendarCells.slice(weekIndex * 6, (weekIndex + 1) * 6);

                                        return (
                                            <div className="rm-air-week" key={`week-${weekIndex}`}>
                                                {weekDateIds.map((dateId, dayIndex) => {
                                                    if (!dateId) {
                                                        return <div key={`blank-${weekIndex}-${dayIndex}`} className="rm-air-date-card is-empty" aria-hidden="true" />;
                                                    }
                                                    const { weekday } = fmtDateShort(dateId);
                                                    const [year, month, dayNumber] = dateId.split("-").map(Number);
                                                    const dateObj = new Date(year, month - 1, dayNumber);
                                                    const monthLabel = dateObj.toLocaleDateString("en-GB", { month: "short" });
                                                    const isSaturday = dateObj.getDay() === 6;
                                                    const isPastDate = dateId < todayId;
                                                    const isToday = dateId === todayId;
                                                    const isDisabledDate = dateId <= todayId;
                                                    const isMonthStart = dateId === visibleDateIds[0];
                                                    const monthFullLabel = dateObj.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

                                                    if (jobTime === "Full day") {
                                                        const availability = getFullDayAvailability(dateId);
                                                        const canBookDate = !isDisabledDate && availability.canBook;
                                                        const isSelected = selection?.dateId === dateId && selection?.bookingSlot === BOOKING_SLOT.FULL_DAY;
                                                        const availableCount = isDisabledDate ? 0 : availability.availableCount;

                                                        return (
                                                            <div
                                                                key={dateId}
                                                                className={`rm-air-date-card ${isPastDate ? "is-past" : ""} ${isToday ? "is-today" : ""} ${isSaturday ? "is-saturday" : ""} ${isMonthStart ? "is-month-start" : ""} ${isSelected ? "is-selected-date" : ""}`}
                                                            >
                                                                {isMonthStart && (
                                                                    <div className="rm-air-new-month-label">
                                                                        <FontAwesomeIcon icon={faCalendarDay} size="xs" />
                                                                        {monthFullLabel}
                                                                    </div>
                                                                )}
                                                                <div className="rm-air-date-head">
                                                                    <div>
                                                                        <div className="rm-air-date-weekday">
                                                                            {weekday}
                                                                            {isToday && <span className="rm-air-today-label">Today</span>}
                                                                        </div>
                                                                        <div className="rm-air-date-number-row">
                                                                            <span className="rm-air-date-number">{String(dayNumber).padStart(2, "0")}</span>
                                                                            <span className="rm-air-date-month">{monthLabel}</span>
                                                                        </div>
                                                                    </div>

                                                                    <span className={`rm-air-slot-badge ${isDisabledDate ? "is-past" : availableCount ? "is-open" : "is-full"}`}>
                                                                        {isToday
                                                                            ? "Unavailable"
                                                                            : isPastDate
                                                                                ? "Past"
                                                                                : availableCount > 0
                                                                                    ? `${availableCount} available`
                                                                                    : "No availability"}
                                                                    </span>
                                                                </div>

                                                                {!isDisabledDate && (
                                                                    <button
                                                                        type="button"
                                                                        disabled={!canBookDate}
                                                                        onClick={() => selectPublicSlot(dateId, BOOKING_SLOT.FULL_DAY, availability)}
                                                                        className={`rm-slot-btn ${canBookDate ? "is-free" : "is-full"} ${availability.choiceLabel === "recommended" ? "is-recommended" : ""} ${availability.choiceLabel === "good" ? "is-good-option" : ""} ${isSelected ? "is-selected" : ""}`}
                                                                    >
                                                                        <span className="rm-air-slot-title-row">
                                                                            <span className="rm-air-slot-title">Full day</span>
                                                                            {availability.choiceLabel === "recommended" && (
                                                                                <span className="rm-choice-pill is-recommended">Recommended</span>
                                                                            )}
                                                                            {availability.choiceLabel === "good" && (
                                                                                <span className="rm-choice-pill is-good">Good option</span>
                                                                            )}
                                                                        </span>
                                                                        <span className="rm-air-slot-status">
                                                                            {availableCount > 0 ? `${availableCount} available` : "Unavailable"}
                                                                        </span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    const morning = getHalfDayAvailability(dateId, BOOKING_SLOT.MORNING);
                                                    const afternoon = getHalfDayAvailability(dateId, BOOKING_SLOT.AFTERNOON);
                                                    const availableCount = isDisabledDate
                                                        ? 0
                                                        : morning.availableCount + afternoon.availableCount;
                                                    const restrictedCount = isDisabledDate
                                                        ? 0
                                                        : morning.blockedCount + afternoon.blockedCount;
                                                    const isCheckingAny = !isDisabledDate && [morning, afternoon].some((slot) => slot.checking);
                                                    const hasFeaturedChoice = !isDisabledDate && [morning, afternoon].some((slot) =>
                                                        slot.choiceLabel === "recommended" || slot.choiceLabel === "good"
                                                    );
                                                    const dateIsSelected = selection?.dateId === dateId;

                                                    const renderHalfDayButton = (slot, availability) => {
                                                        const isSelected = selection?.dateId === dateId && selection?.bookingSlot === slot;
                                                        const relation = availability.candidate?.relation;
                                                        const canBookSlot = !isDisabledDate && availability.canBook && !availability.checking;
                                                        const isRecommended = availability.choiceLabel === "recommended";
                                                        const isGoodOption = availability.choiceLabel === "good";
                                                        const availableLabel = `${availability.availableCount} available`;

                                                        let statusText = availableLabel;
                                                        if (canBookSlot) {
                                                            if (relation === "same") {
                                                                statusText = `${availableLabel} · Same building`;
                                                            } else if (relation === "near") {
                                                                statusText = `${availableLabel} · Nearby`;
                                                            }
                                                        } else {
                                                            statusText = availability.checking
                                                                ? "Checking availability"
                                                                : availability.verificationError
                                                                    ? "Travel check unavailable"
                                                                    : availability.blockedByTravelRule
                                                                        ? "Not suitable"
                                                                        : "Unavailable";
                                                        }

                                                        const title = isToday
                                                            ? "Today cannot be selected. Please choose a future date."
                                                            : isPastDate
                                                                ? "Past dates cannot be selected."
                                                                : availability.blockedByTravelRule
                                                                    ? `No carpenter can safely pair this half-day with the other job under the ${MAX_TRAVEL_MINUTES}-minute travel rule.`
                                                                    : availability.verificationError
                                                                        ? "Travel time could not be verified for the occupied carpenters."
                                                                        : availability.checking
                                                                            ? "Checking live route suitability."
                                                                            : isRecommended
                                                                                ? "Best operational choice for this building."
                                                                                : isGoodOption
                                                                                    ? "A fully valid alternative booking option."
                                                                                    : "Available and bookable.";

                                                        return (
                                                            <button
                                                                type="button"
                                                                disabled={!canBookSlot}
                                                                onClick={() => selectPublicSlot(dateId, slot, availability)}
                                                                title={title}
                                                                className={`rm-slot-btn ${canBookSlot ? "is-free" : "is-full"} ${isRecommended ? "is-recommended" : ""} ${isGoodOption ? "is-good-option" : ""} ${isSelected ? "is-selected" : ""}`}
                                                            >
                                                                <span className="rm-air-slot-title-row">
                                                                    <span className="rm-air-slot-title">{slot}</span>
                                                                    {isRecommended && (
                                                                        <span className="rm-choice-pill is-recommended">Recommended</span>
                                                                    )}
                                                                    {isGoodOption && (
                                                                        <span className="rm-choice-pill is-good">Good option</span>
                                                                    )}
                                                                </span>
                                                                <span className="rm-air-slot-status">{statusText}</span>
                                                            </button>
                                                        );
                                                    };

                                                    return (
                                                        <div
                                                            key={dateId}
                                                            className={`rm-air-date-card ${isPastDate ? "is-past" : ""} ${isToday ? "is-today" : ""} ${isSaturday ? "is-saturday" : ""} ${isMonthStart ? "is-month-start" : ""} ${hasFeaturedChoice ? "has-featured" : ""} ${dateIsSelected ? "is-selected-date" : ""}`}
                                                        >
                                                            {isMonthStart && (
                                                                <div className="rm-air-new-month-label">
                                                                    <FontAwesomeIcon icon={faCalendarDay} size="xs" />
                                                                    {monthFullLabel}
                                                                </div>
                                                            )}
                                                            <div className="rm-air-date-head">
                                                                <div>
                                                                    <div className="rm-air-date-weekday">
                                                                        {weekday}
                                                                        {isToday && <span className="rm-air-today-label">Today</span>}
                                                                    </div>
                                                                    <div className="rm-air-date-number-row">
                                                                        <span className="rm-air-date-number">{String(dayNumber).padStart(2, "0")}</span>
                                                                        <span className="rm-air-date-month">{monthLabel}</span>
                                                                    </div>
                                                                </div>

                                                                <span
                                                                    className={`rm-air-slot-badge ${isDisabledDate ? "is-past" : availableCount > 0 ? "is-open" : isCheckingAny ? "is-checking" : "is-full"}`}
                                                                    title={!isDisabledDate && restrictedCount > 0 ? `${restrictedCount} additional slot${restrictedCount === 1 ? " is" : "s are"} not suitable for this building because of the 30-minute travel rule.` : ""}
                                                                >
                                                                    {isToday
                                                                        ? "Unavailable"
                                                                        : isPastDate
                                                                            ? "Past"
                                                                            : availableCount > 0
                                                                                ? `${availableCount} available`
                                                                                : isCheckingAny
                                                                                    ? "Checking"
                                                                                    : "No availability"}
                                                                </span>
                                                            </div>

                                                            {!isDisabledDate && (
                                                                <div className="rm-air-slot-stack">
                                                                    {renderHalfDayButton(BOOKING_SLOT.MORNING, morning)}
                                                                    {renderHalfDayButton(BOOKING_SLOT.AFTERNOON, afternoon)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {selection && (
                <div className="remedial-card" style={{ padding: "20px 24px" }}>
                    <div style={{
                        display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap",
                        padding: "10px 14px", borderRadius: 10, background: "#fef2f2", border: "1px solid #fecaca",
                    }}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#dc3545" }} />
                        <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#7f1d1d" }}>
                            {fmtDateId(selection.dateId)} · {selection.bookingSlot}
                        </span>
                    </div>

                    <div className="mb-3">
                        <label className="rm-form-label">Notes <span style={{ color: "#adb5bd", fontWeight: 400 }}>(optional)</span></label>
                        <textarea
                            rows={2}
                            className="rm-form-input"
                            placeholder="Anything the carpenter should know..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>

                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <button type="button" className="btn-remedial-outline" onClick={() => setSelection(null)} disabled={saving}>
                            Cancel
                        </button>
                        <button type="button" className="btn-remedial-primary" onClick={handleConfirm} disabled={saving}>
                            {saving ? (
                                <><Spinner animation="border" size="sm" /> Booking...</>
                            ) : (
                                <><FontAwesomeIcon icon={faCheckCircle} /> Confirm Booking</>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const BookRemedial = () => {
    const { buildingId, id: doorId } = useParams();
    const [user] = useAuthState(auth);
    const { isAdmin, loaded: roleLoaded } = useOwnRole(user);

    const isSingleDoorView = Boolean(buildingId && doorId);

    const headerBuildingName = useBuildingName(isSingleDoorView ? buildingId : null);
    const { door: headerDoor, doorType: headerDoorType } = useDoorByPath(
        isSingleDoorView ? buildingId : null,
        isSingleDoorView ? doorId : null
    );
    const headerDoorNumber = headerDoor ? doorLabel(headerDoorType, headerDoor) : "";

    return (
        <div className="book-remedials-wrapper">
            <div className="book-remedials-container">
                <ToastContainer position="top-right" autoClose={3000} theme="colored" />

                {isSingleDoorView && isAdmin && (
                    <div style={{ marginBottom: 14 }}>
                        <Link to="/book-remedial" className="rm-link">
                            <FontAwesomeIcon icon={faArrowLeft} />
                            Back to overview
                        </Link>
                    </div>
                )}

                <div className="remedials-header">
                    <h1 className="remedials-header-title">
                        <FontAwesomeIcon icon={faHammer} className="remedials-header-icon" />
                        {isSingleDoorView
                            ? `Book ${headerBuildingName || "..."}${headerDoorNumber ? ` Flat ${headerDoorNumber}` : ""}`
                            : "Book Remedial Work"}
                    </h1>
                    <p className="remedials-header-subtitle">
                        {isSingleDoorView
                            ? "Select an available date and time slot for this door."
                            : "Doors with completed inspections ready for carpenter scheduling."}
                    </p>
                </div>

                {isSingleDoorView ? (
                    <DoorBookingPage buildingId={buildingId} doorId={doorId} user={user} />
                ) : (
                    <OverviewPage isAdmin={isAdmin} roleLoaded={roleLoaded} />
                )}
            </div>
        </div>
    );
};

export default BookRemedial;